import {
  CircularProgress,
  Dialog,
  MenuItem,
  TextareaAutosize,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import noImage from "assets/images/no-image.png";
import Input from "components/common/Input";
import Select from "components/common/Select";
import api from "config/api";
import { useAppDispatch, useAppSelector } from "hooks/store";
import {
  setCartBtnLoading,
  setPizzaList,
  setSelected,
  toggleCart,
} from "store/slices/main.slice";
import { type UIEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CrustType, SizeType } from "utils/types";
import AddIcon from "@mui/icons-material/Add";
import Button from "components/common/Button";
import RemoveIcon from "@mui/icons-material/Remove";
import ModificationPopUp from "./ModificationPopUp";
import VariantSelection from "components/common/VariantSelection";
import { toast } from "react-toastify";
import { addUpdateToCart } from "store/action/main";
import { flushSync } from "react-dom";
import { getProductOrderTypeAvailability } from "utils/orderTypeAvailability";
import { showApiErrorToast } from "utils/apiToast";

type Props = {};

const DEBUG_DEAL_EDIT = true;

const PRODUCT_POPUP_PLACE_ORDER_BUTTON_CSS = `
  .product-popup-place-order-btn{
    min-height: 48px;
    position: relative;
    overflow: hidden;
    z-index: 1;
    border-radius: 14px !important;
    border: 1px solid rgba(var(--color-primary) / .18) !important;
    background: rgb(var(--color-primary)) !important;
    color: rgb(var(--color-themeSelColor)) !important;
    padding: 12px 24px !important;
    box-shadow: 0 8px 18px rgba(var(--color-primary) / .20) !important;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 14px !important;
    font-weight: 700 !important;
    letter-spacing: 0 !important;
    text-shadow: none;
    transition: all .2s ease-in;
  }
  .product-popup-place-order-btn::before,
  .product-popup-place-order-btn::after{
    content: "";
    position: absolute;
    display: block;
    border-radius: 50%;
    z-index: -1;
    transition: all .5s .1s cubic-bezier(.55, 0, .1, 1);
    pointer-events: none;
  }
  .product-popup-place-order-btn::before{
    left: 50%;
    top: 100%;
    width: 140%;
    height: 180%;
    transform: translateX(-50%) scaleY(1) scaleX(1.25);
    background-color: rgba(var(--color-primary) / .08);
  }
  .product-popup-place-order-btn::after{
    left: 55%;
    top: 180%;
    width: 160%;
    height: 190%;
    transform: translateX(-50%) scaleY(1) scaleX(1.45);
    background-color: rgb(var(--color-primary));
  }
  .product-popup-place-order-btn:hover{
    color: #ffffff !important;
    border-color: rgba(var(--color-primary) / .26) !important;
    background: rgb(var(--color-primary)) !important;
    filter: brightness(.78) saturate(1.08);
    box-shadow: 0 10px 20px rgba(var(--color-primary) / .24) !important;
  }
  .product-popup-place-order-btn:hover::before{
    opacity: 0;
  }
  .product-popup-place-order-btn:hover::after{
    opacity: 0;
  }
  .product-popup-place-order-btn:active{
    color: rgb(var(--color-themeSelColor)) !important;
    filter: brightness(.82);
    box-shadow: inset 0 2px 8px rgba(0,0,0,.18) !important;
  }
  .product-popup-place-order-btn:disabled{
    cursor: not-allowed;
    color: rgba(16,24,40,.50) !important;
    background: #ececec !important;
    border-color: #ececec !important;
    box-shadow: none !important;
    opacity: 1 !important;
  }
  .product-popup-place-order-btn:disabled::before,
  .product-popup-place-order-btn:disabled::after{
    display: none;
  }
`;

const debugDealEdit = (label: string, payload?: any) => {
  if (!DEBUG_DEAL_EDIT) return;
  console.log(`[DEAL-EDIT] ${label}`, payload);
};

const initialConfig = {
  qty: 1,
  size: 0,
  crust: 0,
  note: "",
  id: 0,
  pizzaName: "",
  pizzaProductCode: "",
  spiceLevel: "",
  modificationsDirty: false,
  modifications: undefined,
  variant: [],
};

const createInitialPizzaConfig = () => ({
  ...initialConfig,
  variant: [],
});

const buildDefaultModifierConfig = (categories: any[] = []) =>
  categories.reduce((acc: any, category: any) => {
    acc[category.modifierId] = (category.modifiers || []).map((modifier: any) => ({
      ...modifier,
      quantity: getModifierQuantity(modifier),
      pizzaRegion: getModifierRegion(modifier),
      selected:
        typeof modifier?.selected === "boolean"
          ? modifier.selected
          : Number(modifier?.defaultTopping) === 1,
    }));
    return acc;
  }, {});

const toTitleCase = (value: any) =>
  String(value || "")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const preferNonEmptyString = (...values: any[]) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }

  return String(values[0] ?? "");
};

const extractRemovedDefaultToppings = (note: any) =>
  String(note || "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => /^\*\*\s*no\s*-/i.test(line))
    .map((line) => line.replace(/^\*\*\s*no\s*-/i, "").trim().toUpperCase());

const stripAutoRemovedToppingLines = (note: any) =>
  String(note || "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line && !/^\*\*\s*no\s*-/i.test(line))
    .join("\n")
    .trim();

const stripAutoToppingSummaryLines = (note: any) =>
  String(note || "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line && !/^toppings\s*:/i.test(line))
    .join("\n")
    .trim();

const stripAutoPizzaNoteLines = (note: any) =>
  stripAutoToppingSummaryLines(stripAutoRemovedToppingLines(note));

const formatToppingNoteName = (value: any) =>
  toTitleCase(value)
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_");

const getModifierRegion = (item: any) =>
  Number(
    item?.pizzaRegion ??
      item?.PizzaRegion ??
      item?.pizzaregion ??
      item?.DtltoppRegionid ??
      item?.dtltopp_regionid ??
      1,
  );

// Keep the old whole-unit topping count available for free-topping calculation
// and display labels. API pricing uses side-adjusted helpers below.
const getLegacyModifierQuantity = (item: any) => {
  if (
    Number(item?.quantity || 0) === 1 &&
    Number(item?.legacyQuantity || 0) <= 1
  ) {
    return 1;
  }

  const explicitMultiplier = Number(
    item?.Multiplier ?? item?.multiplier ?? item?.DtltoppMultiplier ?? 0,
  );
  if (explicitMultiplier > 1) {
    return explicitMultiplier;
  }
  if (Number(item?.legacyQuantity || 0) > 1) return Number(item.legacyQuantity);
  if (Number(item?.isMultiplier ?? item?.DtltoppIsmultiplier ?? 0) > 0) return 2;
  const quantity = Number(item?.quantity || 1);
  return quantity === 2 ? 2 : 1;
};

const getModifierQuantity = getLegacyModifierQuantity;

const isServiceSubProduct = (item: any) =>
  Number(item?.isServiceSub ?? item?.IsServiceSub ?? 0) === 1;

const isHalfHalfNotAllowedProduct = (item: any) =>
  Number(item?.isHalfHalfNotAllowed ?? item?.IsHalfHalfNotAllowed ?? 0) === 1;

const getModifierMultiplier = (item: any) => Math.max(1, getLegacyModifierQuantity(item));

const getModifierIsMultiplier = (item: any) =>
  getModifierMultiplier(item) > 1 ? 1 : 0;

const getModifierSideFactor = (item: any) => {
  const region = getModifierRegion(item);
  return region === 2 || region === 3 ? 0.5 : 1;
};

const getModifierSelectionUnitCount = (item: any) =>
  Number.isFinite(Number(item?.selectionUnitCount ?? item?.toppingUnitCount)) &&
  Number(item?.selectionUnitCount ?? item?.toppingUnitCount) >= 0
    ? Number(item?.selectionUnitCount ?? item?.toppingUnitCount)
    : item?.selected
    ? Number(item?.defaultTopping || 0) === 1 && getModifierQuantity(item) > 1
      ? 2
      : 1
    : 0;

const getFreeSelectionUnitsForChargeableUnit = (
  remainingFreeUnits: number,
  selectionUnits: number,
  sideFactor: number,
) => {
  const remaining = Number(remainingFreeUnits || 0);
  const selectedUnits = Number(selectionUnits || 0);
  const chargeUnit = Number(sideFactor || 0);

  if (remaining <= 0 || selectedUnits <= 0 || chargeUnit <= 0) return 0;
  if (remaining >= selectedUnits) return selectedUnits;

  const partialFreeUnits = Math.floor(remaining / chargeUnit) * chargeUnit;
  return Math.min(selectedUnits, partialFreeUnits);
};

const getModifierApiQuantity = (item: any) => getModifierSideFactor(item);

const getModifierApiUnitPrice = (item: any, amount = 0) =>
  +Number(amount || 0).toFixed(2);

const getModifierApiSubTotal = (item: any, amount = 0, chargedQuantity = 1) =>
  +(
    Number(amount || 0) *
    Number(
      getModifierSideFactor(item) < 1
        ? Math.min(1, Number(chargedQuantity || 0))
        : chargedQuantity || 0,
    )
  ).toFixed(2);

const getModifierLineAmount = (item: any) =>
  Number(item?.subTotal ?? item?.totalValue ?? 0);

const formatModifierNoteAmount = (value: any) => {
  const amount = Number(value || 0);
  if (!amount) return "";
  return Number.isInteger(amount) ? String(amount) : String(+amount.toFixed(2));
};

const buildModifierNote = (item: any) => {
  const name = String(
    item?.pizzaModifierName || item?.modifierName || item?.description || "",
  ).trim();
  if (!name) return String(item?.note || "");

  const region = getModifierRegion(item);
  const sidePrefix = region === 2 ? "L-" : region === 3 ? "R-" : "";
  const quantityPrefix = getModifierQuantity(item) === 2 ? "2x " : "";
  const amount = formatModifierNoteAmount(item?.subTotal ?? item?.totalValue);

  return `${sidePrefix}${quantityPrefix}${name}${amount ? ` ${amount}` : ""}`;
};

const formatToppingNoteItem = (item: any) => {
  const name = formatToppingNoteName(
    item?.pizzaModifierName || item?.modifierName || item?.description,
  );

  if (!name) return "";
  return getModifierQuantity(item) === 2 ? `2x ${name}` : name;
};

const formatToppingDisplayItem = (item: any) => {
  const name = toTitleCase(
    item?.pizzaModifierName || item?.modifierName || item?.description,
  );

  if (!name) return "";
  return getModifierQuantity(item) === 2 ? `2x ${name}` : name;
};

const buildToppingDisplayGroups = (items: any[] = []) => {
  const groups = {
    whole: [] as string[],
    left: [] as string[],
    right: [] as string[],
  };

  items.forEach((item: any) => {
    const formatted = formatToppingDisplayItem(item);
    if (!formatted) return;

    const region = getModifierRegion(item);
    if (region === 2) {
      groups.left.push(formatted);
    } else if (region === 3) {
      groups.right.push(formatted);
    } else {
      groups.whole.push(formatted);
    }
  });

  return groups;
};

const buildToppingSummaryNoteLine = (modifications: any) => {
  if (!modifications || typeof modifications !== "object") return "";

  const whole: string[] = [];
  const left: string[] = [];
  const right: string[] = [];

  Object.values(modifications)
    .flat()
    .filter((item: any) => item?.selected)
    .forEach((item: any) => {
      const formatted = formatToppingNoteItem(item);
      if (!formatted) return;

      const region = getModifierRegion(item);
      if (region === 2) {
        left.push(formatted);
      } else if (region === 3) {
        right.push(formatted);
      } else {
        whole.push(formatted);
      }
    });

  const parts = [...whole];
  if (left.length) parts.push(`[L-${left.join(", ")}]`);
  if (right.length) parts.push(`[R-${right.join(", ")}]`);

  return parts.length ? `Toppings: ${parts.join(", ")}` : "";
};

const buildSavedModifierConfig = (categories: any[] = [], dealPizza: any) => {
  const savedModifiers = Array.isArray(dealPizza?.savedCartModifiers)
    ? dealPizza.savedCartModifiers
    : Array.isArray(dealPizza?.modifiers)
    ? dealPizza.modifiers
    : [];
  const savedMap = new Map(
    savedModifiers.map((item: any) => [
      Number(item?.modifierId || item?.pizzaModifierId),
      item,
    ]),
  );
  const removedDefaults = new Set(
    extractRemovedDefaultToppings(
      preferNonEmptyString(
        dealPizza?.existingFullNote,
        dealPizza?.sourceDealPizza?.note,
        dealPizza?.note,
        "",
      ),
    ),
  );

  return categories.reduce((acc: any, category: any) => {
    acc[category.modifierId] = (category.modifiers || []).map((modifier: any) => {
      const modifierId = Number(modifier?.pizzaModifierId || modifier?.modifierId || 0);
      const saved: any = savedMap.get(modifierId);
      const modifierName = String(
        modifier?.pizzaModifierName || modifier?.modifierName || saved?.description || "",
      )
        .trim()
        .toUpperCase();
      const isRemovedDefault =
        Number(modifier?.defaultTopping) === 1 && removedDefaults.has(modifierName);
      const selected = isRemovedDefault
        ? false
        : saved
        ? true
        : typeof modifier?.selected === "boolean"
        ? modifier.selected
        : Number(modifier?.defaultTopping) === 1;

      return {
        ...modifier,
        quantity: getModifierQuantity(saved || modifier),
        pizzaRegion: getModifierRegion(saved || modifier),
        selected,
      };
    });
    return acc;
  }, {});
};

const mapSavedCartModifiersForSubmit = (savedModifiers: any[] = [], pizzaItemNumber?: number) =>
  savedModifiers.map((item: any) => ({
    defaultTopping: item.defaultTopping,
    newDefaultTopping: item.newDefaultTopping,
    modifierId: item.modifierId || item.pizzaModifierId,
    description: item.description || item.pizzaModifierName || item.modifierName,
    legacyQuantity: item.legacyQuantity,
    quantity: item.quantity || 1,
    isMultiplier: Number(item.isMultiplier ?? item.DtltoppIsmultiplier ?? 0),
    Multiplier: Number(item.Multiplier || item.DtltoppMultiplier || item.legacyQuantity || 1),
    multiplier: Number(item.multiplier || item.Multiplier || item.DtltoppMultiplier || item.legacyQuantity || 1),
    unitPrice: Number(item.unitPrice || 0),
    subTotal: Number(item.subTotal || 0),
    totalValue: Number(item.subTotal ?? item.totalValue ?? 0),
    note: buildModifierNote(item),
    isFree: typeof item.isFree === "number" ? item.isFree : Number(item.unitPrice || 0) > 0 ? 0 : 1,
    pizzaRegion: item.pizzaRegion ?? item.PizzaRegion ?? 1,
    PizzaRegion: item.PizzaRegion ?? item.pizzaRegion ?? 1,
    PizzaNo: String(pizzaItemNumber || item.PizzaNo || item.dtltopp_pizzaitem || item.DtltoppPizzaitem || ""),
  }));

const mapDealPizzaModifierForSubmit = (item: any, pizzaItemNumber: number) => ({
  defaultTopping: item.defaultTopping,
  newDefaultTopping: item.newDefaultTopping,
  modifierId: item.modifierId || item.pizzaModifierId,
  description: item.description || item.pizzaModifierName || item.modifierName,
  legacyQuantity: item.legacyQuantity,
  quantity: item.quantity || 1,
  isMultiplier: getModifierIsMultiplier(item),
  Multiplier: getModifierMultiplier(item),
  multiplier: getModifierMultiplier(item),
  unitPrice: Number(item.unitPrice || 0),
  subTotal: Number(item.subTotal || 0),
  totalValue: Number(item.subTotal ?? item.totalValue ?? 0),
  note: buildModifierNote(item),
  isFree: item.isFree,
  pizzaRegion: getModifierRegion(item),
  PizzaRegion: getModifierRegion(item),
  PizzaNo: String(pizzaItemNumber),
});

const mapDealPizzaToConfig = (dealPizza: any) => {
  if (dealPizza?.config) {
    return {
      ...createInitialPizzaConfig(),
      ...dealPizza.config,
      note: stripAutoPizzaNoteLines(
        preferNonEmptyString(dealPizza?.config?.note, dealPizza?.note, ""),
      ),
      savedCartModifiers: Array.isArray(dealPizza?.modifiers) ? dealPizza.modifiers : [],
      modificationsDirty: false,
      existingFullNote: preferNonEmptyString(dealPizza?.note, dealPizza?.config?.note, ""),
      existingPizzaSize: dealPizza?.pizzaSize ?? dealPizza?.config?.existingPizzaSize ?? null,
      existingSizePrice: Number(
        dealPizza?.config?.existingSizePrice ?? dealPizza?.pizzaSize?.price ?? 0,
      ),
      existingPizzaCrust: dealPizza?.pizzaCrust ?? dealPizza?.config?.existingPizzaCrust ?? null,
      existingSpiceLevel: dealPizza?.spiceLevel ?? dealPizza?.config?.existingSpiceLevel ?? null,
      existingSubTotal: Number(
        dealPizza?.subTotal ?? dealPizza?.config?.existingSubTotal ?? 0,
      ),
      sourceDealPizza: dealPizza,
      variant: Array.isArray(dealPizza.config?.variant)
        ? dealPizza.config.variant
        : [],
    };
  }

  return {
    ...createInitialPizzaConfig(),
    id: Number(dealPizza?.id || 0),
    pizzaName: dealPizza?.pizzaName ?? dealPizza?.fullDescription ?? "",
    pizzaProductCode: dealPizza?.productCode || "",
    size: Number(dealPizza?.pizzaSize?.pizzasizeId || 0),
    crust: Number(dealPizza?.crust || 0),
    note: stripAutoPizzaNoteLines(preferNonEmptyString(dealPizza?.note, "")),
    spiceLevel: dealPizza?.spiceLevel?.spicename || "",
    savedCartModifiers: Array.isArray(dealPizza?.modifiers) ? dealPizza.modifiers : [],
    modificationsDirty: false,
    modifications: undefined,
    existingFullNote: preferNonEmptyString(dealPizza?.note, ""),
    existingPizzaSize: dealPizza?.pizzaSize ?? null,
    existingSizePrice: Number(dealPizza?.pizzaSize?.price || 0),
    existingPizzaCrust: dealPizza?.pizzaCrust ?? null,
    existingSpiceLevel: dealPizza?.spiceLevel ?? null,
    existingSubTotal: Number(dealPizza?.subTotal || 0),
    sourceDealPizza: dealPizza,
  };
};

const isDealProduct = (product: any) => Number(product?.isDeal || 0) === 1;
const getDealPizzaCount = (product: any) =>
  Math.max(
    0,
    Number(
      product?.pizzasInDeal ??
        product?.PizzasInDeal ??
        product?.pizzaInDeal ??
        product?.PizzaInDeal ??
        product?.pizzas ??
        product?.Pizzas ??
        0,
    ),
  );
const hasDealPizzaSlots = (product: any) => getDealPizzaCount(product) > 0;

const isDealPizzaDropdownItem = (item: any) =>
  Number(item?.isPizza || 0) === 1 &&
  Number(item?.isDeal || 0) !== 1 &&
  Number(item?.isCreateYourOwn || 0) !== 1 &&
  Number(item?.productId || 0) > 0;

const mergePizzaProducts = (existing: any[] = [], incoming: any[] = []) => {
  const pizzaMap = new Map<number, any>();

  [...existing, ...incoming].forEach((item: any) => {
    if (!isDealPizzaDropdownItem(item)) return;
    const productId = Number(item?.productId || 0);
    if (!pizzaMap.has(productId)) pizzaMap.set(productId, item);
  });

  return Array.from(pizzaMap.values()).sort((a: any, b: any) =>
    String(a?.productName || "").localeCompare(String(b?.productName || ""))
  );
};

const ProductPopUp = (props: Props) => {
  const dispatch = useAppDispatch();

  // === ADDED BY SAMPATH – 2026-03-16 ===
  // Mobile full-screen dialog support
  const theme = useTheme();
  const isMobilePopup = useMediaQuery(theme.breakpoints.down("sm"));
  // === END SAMPATH ===

  const [selectedProduct, setSelectedProduct] = useState<any>(undefined);
  const hideServiceSubPizzaOptions = isServiceSubProduct(selectedProduct);
  const hideHalfHalfToppingOptions = isHalfHalfNotAllowedProduct(selectedProduct);
  const dealSlotLabel = hideServiceSubPizzaOptions ? "Sub" : "Pizza";
  const [variants, setVariants] = useState<any>([]);
  const [sizes, setSizes] = useState<SizeType[]>([]);
  const [crusts, setCrusts] = useState<CrustType[]>([]);
  const [showModification, setshowModification] = useState<any>(undefined);
  const [config, setConfig] = useState(initialConfig);
  const [dealVariantConfig, setDealVariantConfig] = useState<any[]>([]);
  const [currentPizzaIndex, setCurrentPizzaIndex] = useState(0);
  const [pizzasInDeal, setPizzasInDeal] = useState<any[]>([]);
  const [spiceData, setSpiceData] = useState<any[]>([]);
  const [checkCartConfigData, setCheckCartConfigData] = useState<number[]>([]);
  const [offerScrollState, setOfferScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });
  const [dealPizzaOptions, setDealPizzaOptions] = useState<any[]>([]);
  const [dealPizzaOptionsLoading, setDealPizzaOptionsLoading] = useState(false);
  const [showScrolledProductHeader, setShowScrolledProductHeader] = useState(false);
  const [expandedDealPizzaNotes, setExpandedDealPizzaNotes] = useState<Record<number, boolean>>({});

  // === ADDED BY SAMPATH – 2026-01-05 ===
  // Product image click -> open image preview popup
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  // === END SAMPATH ===
  const configRef = useRef<any>(initialConfig);
  const pizzasInDealRef = useRef<any[]>([]);
  const modifierLoadKeyRef = useRef("");
  const offerScrollRef = useRef<HTMLDivElement | null>(null);
  const dealPizzaFetchKeyRef = useRef("");
  const modifierPriceCacheRef = useRef<Map<string, number>>(new Map());

  const handleProductPopupScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const shouldShowHeader = event.currentTarget.scrollTop > 190;
      setShowScrolledProductHeader((current) =>
        current === shouldShowHeader ? current : shouldShowHeader,
      );
    },
    [],
  );

  // === ADDED BY SAMPATH – 2026-01-05 ===
  // FIX (All devices): Prevent "The object can not be cloned." on 2nd edit.
  // structuredClone sometimes fails when state contains non-cloneable values.
  // This safeClone supports Set/Map/Date and removes functions safely.
  const safeClone = useCallback((obj: any) => {
    try {
      return structuredClone(obj);
    } catch (_err) {
      const seen = new WeakMap<object, any>();

      const cloneRec = (v: any): any => {
        if (v === null || typeof v !== "object") return v;

        // structuredClone fails on functions
        if (typeof v === "function") return undefined;

        if (v instanceof Date) return new Date(v.getTime());

        if (v instanceof Set) {
          const out = new Set<any>();
          v.forEach((item) => out.add(cloneRec(item)));
          return out;
        }

        if (v instanceof Map) {
          const out = new Map<any, any>();
          v.forEach((val, key) => out.set(cloneRec(key), cloneRec(val)));
          return out;
        }

        if (Array.isArray(v)) return v.map(cloneRec);

        if (seen.has(v)) return seen.get(v);

        const out: any = {};
        seen.set(v, out);

        Object.keys(v).forEach((k) => {
          const cv = cloneRec(v[k]);
          if (cv !== undefined) out[k] = cv;
        });

        return out;
      };

      return cloneRec(obj);
    }
  }, []);

  const clonePizzaConfig = useCallback(
    (pizzaConfig: any) => (pizzaConfig ? safeClone(pizzaConfig) : createInitialPizzaConfig()),
    [safeClone],
  );

  const updateDealPizzaSlot = useCallback(
    (slotIndex: number, pizzaConfig: any) => {
      const cloned = clonePizzaConfig(pizzaConfig);
      const nextFromRef = [...pizzasInDealRef.current];
      nextFromRef[slotIndex] = cloned;
      pizzasInDealRef.current = nextFromRef;

      setPizzasInDeal((prev) => {
        const next = [...prev];
        next[slotIndex] = cloned;
        return next;
      });
    },
    [clonePizzaConfig],
  );

  const updateCurrentPizzaConfig = useCallback(
    (updater: any) => {
      setConfig((prev: any) => {
        const next =
          typeof updater === "function"
            ? updater(prev)
            : {
                ...prev,
                ...updater,
              };

        if (hasDealPizzaSlots(selectedProduct)) {
          updateDealPizzaSlot(currentPizzaIndex, next);
        }

        configRef.current = next;
        return next;
      });
    },
    [currentPizzaIndex, selectedProduct, updateDealPizzaSlot],
  );

  const updatePopupQuantity = useCallback(
    (nextQty: any) => {
      const qty = Math.min(9999, Math.max(1, Number(nextQty) || 1));

      if (hasDealPizzaSlots(selectedProduct)) {
        setPizzasInDeal((prev) => {
          const next = [...prev];
          const current = clonePizzaConfig(configRef.current);
          next[currentPizzaIndex] = current;
          const updated = next.map((pizza: any) =>
            clonePizzaConfig({
              ...(pizza || createInitialPizzaConfig()),
              qty,
            }),
          );
          pizzasInDealRef.current = updated;
          return updated;
        });
      }

      setConfig((prev: any) => {
        const next = {
          ...prev,
          qty,
        };
        configRef.current = next;
        return next;
      });
    },
    [clonePizzaConfig, currentPizzaIndex, selectedProduct],
  );
  // === END SAMPATH ===

  const normalizePizzaSizes = (items: any[] = []) => {
    const uniqueSizes = new Map<string, any>();

    items.forEach((item) => {
      const key = [
        String(item?.sizeId || "").trim(),
        String(item?.sizeName || "").trim().toLowerCase(),
        String(item?.sizePriceX1 || 0).trim(),
      ].join("|");

      // Remove only exact duplicate rows, but keep same size name if price is different.
      if (!uniqueSizes.has(key)) {
        uniqueSizes.set(key, item);
      }
    });

    return Array.from(uniqueSizes.values()).sort((a: any, b: any) => {
      const sizeOrder = Number(a?.sizeIndex || 0) - Number(b?.sizeIndex || 0);
      if (sizeOrder !== 0) return sizeOrder;

      return Number(a?.sizePriceX1 || 0) - Number(b?.sizePriceX1 || 0);
    });
  };

  const product = useAppSelector(({ main }) => main.selectedProduct);
  const {
    settings,
    deliveryType,
    cartBtnLoading,
    taxes,
    pizzaList,
    showCart,
    sideBarMenu,
  } =
    useAppSelector(({ main }) => main);
  const cartOpenTimeoutRef = useRef<number | null>(null);

  const ENABLE_SPL_DISCOUNT_BADGE = false;
  const specialDiscountPercent = Number(settings?.OnlineSpecialDiscount || 0);
  const activeOrderType = deliveryType === "pickup" ? "TakeOut" : "Delivery";
  const showSpecialDiscountBadge =
    ENABLE_SPL_DISCOUNT_BADGE &&
    specialDiscountPercent > 0 &&
    !window.location.pathname.includes("admin") &&
    ((activeOrderType === "Delivery" &&
      !!Number(settings?.AllowDiscountOnDelivery)) ||
      (activeOrderType === "TakeOut" &&
        !!Number(settings?.AllowDiscountOnPickup)));

  // === ADDED BY SAMPATH – 2026-01-05 ===
  // Safe helper for UI price display (design only)
  const formatMoney = (v: any) => {
    const n = Number(v);
    if (Number.isNaN(n)) return "$0.00";
    return `$${n.toFixed(2)}`;
  };

const formatToppingCount = (value: any) => {
  const count = Number(value || 0);
  return Number.isInteger(count) ? String(count) : String(+count.toFixed(2));
};

const getToppingPriceTiers = (sizeItem: any) =>
  Object.entries(sizeItem || {})
    .map(([key, value]: any) => {
      const match = /^toppingPrice(\d+)$/i.exec(key);
      return match && Number(value || 0) > 0
        ? { count: Number(match[1]), price: Number(value) }
        : null;
    })
    .filter(Boolean)
    .sort((a: any, b: any) => a.count - b.count) as {
    count: number;
    price: number;
  }[];
  // === END SAMPATH ===

  const getModifierAmount = (item: any) => {
    const candidates = [
      item?.pizzaModifierPrice,
      item?.basePizzaModifierPrice,
      item?.modifierPrice,
      item?.pizzaModifierCost,
      item?.modifierCost,
      item?.unitPrice,
      item?.UnitPrice,
      item?.extraPrice,
      item?.price,
      item?.amount,
      item?.extraAmount,
      item?.toppingPrice,
      item?.additionalToppingPrice,
      item?.DtltoppPrice,
      item?.dtltopp_price,
    ];

    const positive = candidates.find((value) => Number(value || 0) > 0);
    if (positive !== undefined && positive !== null) return Number(positive);

    const found = candidates.find(
      (value) => value !== undefined && value !== null && !Number.isNaN(Number(value)),
    );
    return Number(found || 0);
  };

  const getModifierIdentity = useCallback((item: any) =>
    String(
      item?.pizzaModifierId ||
        item?.modifierId ||
        item?.DtltoppToppingid ||
        item?.id ||
        item?.pizzaModifierName ||
        item?.modifierName ||
        item?.description ||
        "",
    ), []);

  const cacheModifierPrices = useCallback(
    (modifications: any) => {
      if (!modifications || typeof modifications !== "object") return;

      Object.values(modifications).forEach((items: any) => {
        if (!Array.isArray(items)) return;

        items.forEach((item: any) => {
          const key = getModifierIdentity(item);
          const amount = getModifierAmount(item);
          if (key && amount > 0) {
            modifierPriceCacheRef.current.set(key, amount);
          }
        });
      });
    },
    [getModifierIdentity],
  );

  const getModifierChargeAmount = useCallback(
    (item: any, fallbackAmount = 0) => {
      const amount = getModifierAmount(item);
      if (amount > 0) return amount;

      const cachedAmount = modifierPriceCacheRef.current.get(getModifierIdentity(item)) || 0;
      return cachedAmount > 0 ? cachedAmount : fallbackAmount;
    },
    [getModifierIdentity],
  );

  const activeDealBaseProductId = Number(selectedProduct?.productId || 0);
  const activePizzaItemProductId = Number(
    (
      selectedProduct?.isPizza
        ? config.id || selectedProduct?.productId
        : selectedProduct?.productId
    ) || 0
  );

  const getTotalFreeToppingCount = useCallback(
    () =>
      Math.max(
        0,
        Number(
          selectedProduct?.totalCombinetoppings ??
            selectedProduct?.totalCombineToppings ??
            selectedProduct?.TotalCombinetoppings ??
            selectedProduct?.TotalCombineToppings ??
            selectedProduct?.CombinedTopping ??
            selectedProduct?.combinedTopping ??
            0,
        ),
      ),
    [selectedProduct],
  );

  const getFreeToppingsPerPizzaCount = useCallback(() => {
    const totalFreeToppings = getTotalFreeToppingCount();
    const dealPizzaCount = getDealPizzaCount(selectedProduct);

    if (dealPizzaCount > 1) {
      return Math.max(0, Math.floor(totalFreeToppings / dealPizzaCount));
    }

    return totalFreeToppings;
  }, [getTotalFreeToppingCount, selectedProduct]);

  const getSelectedModifierCategories = useCallback((modifications: any) => {
    if (!modifications || typeof modifications !== "object") return [];

    return Object.entries(modifications).map(([categoryId, items]: any) => ({
      categoryId: Number(categoryId),
      freeLimit: Array.isArray(items)
        ? items.reduce(
            (sum: number, item: any) =>
              sum + (Number(item?.defaultTopping) === 1 ? 1 : 0),
            0,
          )
        : 0,
      items: Array.isArray(items) ? items.filter((item: any) => item?.selected) : [],
    }));
  }, []);

  const getPricedSelectedModifierUnitCountFromItems = useCallback(
    (items: any[] = []) =>
      items
        .filter((item: any) => getModifierAmount(item) > 0)
        .reduce(
          (sum: number, item: any) => sum + getModifierSelectionUnitCount(item),
          0,
        ),
    [],
  );

  const getExtraToppingUnitPrice = useCallback((sizeItem: any) => {
    if (!sizeItem) return 0;

    const directCandidates = [
      sizeItem?.extraToppingPrice,
      sizeItem?.additionalToppingPrice,
      sizeItem?.additionalTopping,
      sizeItem?.toppingExtraPrice,
      sizeItem?.extraPrice,
      sizeItem?.modifierCost,
      sizeItem?.pizzaModifierCost,
      sizeItem?.unitPrice,
      sizeItem?.UnitPrice,
      sizeItem?.modifierPrice,
      sizeItem?.pizzaModifierPrice,
    ];
    const directPrice = directCandidates.find((value) => Number(value || 0) > 0);
    if (directPrice) return Number(directPrice);

    const tiers = Object.entries(sizeItem || {})
      .map(([key, value]: any) => {
        const match = /^toppingPrice(\d+)$/i.exec(key);
        return match && Number(value || 0) > 0
          ? { count: Number(match[1]), price: Number(value) }
          : null;
      })
      .filter(Boolean)
      .sort((a: any, b: any) => a.count - b.count) as {
      count: number;
      price: number;
    }[];

    for (let index = 1; index < tiers.length; index += 1) {
      const diff = +(tiers[index].price - tiers[index - 1].price).toFixed(2);
      if (diff > 0) return diff;
    }

    return 0;
  }, []);

  const getPricedSelectedModifiers = useCallback(
    (modifications: any) => {
      const selectedCategories = getSelectedModifierCategories(modifications);
      const shouldUseCombinedFree =
        Number(selectedProduct?.isCreateYourOwn || 0) === 1;

      if (shouldUseCombinedFree) {
        const allItems = selectedCategories.flatMap(({ items }: any) => items);
        const toChargeableUnits = (sourceItems: any[] = []) =>
          sourceItems
            .filter((item: any) => getModifierChargeAmount(item) > 0)
            .map((item: any, orderIndex: number) => ({
              item,
              orderIndex,
              selectionUnits: getModifierSelectionUnitCount(item),
              chargeSelectionUnits: getModifierQuantity(item) * getModifierSideFactor(item),
              legacyQuantity: getModifierQuantity(item),
              sideFactor: getModifierSideFactor(item),
            }));
        const sortChargeableUnits = (sourceItems: any[] = []) =>
          [...sourceItems].sort(
            (a: any, b: any) =>
              getModifierChargeAmount(a.item) - getModifierChargeAmount(b.item) ||
              a.orderIndex - b.orderIndex,
          );
        const defaultUnits = toChargeableUnits(
          allItems.filter((item: any) => Number(item?.defaultTopping || 0) === 1),
        );
        const extraUnits = sortChargeableUnits(
          toChargeableUnits(
            allItems.filter((item: any) => Number(item?.defaultTopping || 0) !== 1),
          ),
        );
        const chargedUnitCounts = [...defaultUnits, ...extraUnits].reduce((state: any, unit: any) => {
          const chargeSelectionUnits = Number(unit.chargeSelectionUnits ?? unit.selectionUnits);
          const isDefaultTopping = Number(unit.item?.defaultTopping || 0) === 1;
          const freeUnits = isDefaultTopping
            ? Math.min(chargeSelectionUnits, unit.sideFactor)
            : getFreeSelectionUnitsForChargeableUnit(
                state.remainingFreeUnits,
                unit.selectionUnits,
                unit.sideFactor,
              );
          const chargedSelectionUnits = Math.max(0, chargeSelectionUnits - freeUnits);
          const chargedQuantity =
            unit.sideFactor > 0 ? chargedSelectionUnits / unit.sideFactor : 0;
          const modifierKey = getModifierIdentity(unit.item);
          if (chargedQuantity > 0) {
            state.map.set(modifierKey, (state.map.get(modifierKey) || 0) + chargedQuantity);
          }
          state.remainingFreeUnits = Math.max(0, state.remainingFreeUnits - freeUnits);
          return state;
        }, {
          map: new Map<string, number>(),
          remainingFreeUnits: getFreeToppingsPerPizzaCount(),
        }).map;

        return allItems.map((item: any) => {
          const modifierAmount = getModifierChargeAmount(item);
          const quantity = getModifierQuantity(item);
          const apiUnitPrice = getModifierApiUnitPrice(item, modifierAmount);
          const apiQuantity = getModifierApiQuantity(item);
          const chargedQuantity = chargedUnitCounts.get(getModifierIdentity(item)) || 0;
          const chargedSubTotal = getModifierApiSubTotal(
            item,
            modifierAmount,
            chargedQuantity,
          );
          const hasFreeQuantity = quantity > chargedQuantity;

          return {
            ...item,
            legacyQuantity: quantity,
            quantity: apiQuantity,
            newDefaultTopping:
              !item?.defaultTopping && item?.selected && hasFreeQuantity ? 1 : 0,
            unitPrice: apiUnitPrice,
            subTotal: chargedSubTotal,
            totalValue: chargedSubTotal,
            isFree: chargedQuantity > 0 ? 0 : 1,
            pizzaRegion: getModifierRegion(item),
            PizzaRegion: getModifierRegion(item),
            isMultiplier: quantity > 1 ? 1 : 0,
            Multiplier: quantity,
            multiplier: quantity,
          };
        });
      }

      return selectedCategories.flatMap(({ items, freeLimit }: any) => {
        const toChargeableUnits = (sourceItems: any[] = []) =>
          sourceItems
            .filter((item: any) => getModifierChargeAmount(item) > 0)
            .map((item: any, orderIndex: number) => ({
              item,
              orderIndex,
              selectionUnits: getModifierSelectionUnitCount(item),
              chargeSelectionUnits: getModifierQuantity(item) * getModifierSideFactor(item),
              legacyQuantity: getModifierQuantity(item),
              sideFactor: getModifierSideFactor(item),
            }));
        const sortChargeableUnits = (sourceItems: any[] = []) =>
          [...sourceItems].sort(
            (a: any, b: any) =>
              getModifierChargeAmount(a.item) - getModifierChargeAmount(b.item) ||
              a.orderIndex - b.orderIndex,
          );
        const defaultUnits = toChargeableUnits(
          items.filter((item: any) => Number(item?.defaultTopping || 0) === 1),
        );
        const extraUnits = sortChargeableUnits(
          toChargeableUnits(
            items.filter((item: any) => Number(item?.defaultTopping || 0) !== 1),
          ),
        );
        const chargedUnitCounts = [...defaultUnits, ...extraUnits].reduce((state: any, unit: any) => {
          const chargeSelectionUnits = Number(unit.chargeSelectionUnits ?? unit.selectionUnits);
          const isDefaultTopping = Number(unit.item?.defaultTopping || 0) === 1;
          const freeUnits = isDefaultTopping
            ? Math.min(chargeSelectionUnits, unit.sideFactor)
            : getFreeSelectionUnitsForChargeableUnit(
                state.remainingFreeUnits,
                unit.selectionUnits,
                unit.sideFactor,
              );
          const chargedSelectionUnits = Math.max(0, chargeSelectionUnits - freeUnits);
          const chargedQuantity =
            unit.sideFactor > 0 ? chargedSelectionUnits / unit.sideFactor : 0;
          const modifierKey = getModifierIdentity(unit.item);
          if (chargedQuantity > 0) {
            state.map.set(modifierKey, (state.map.get(modifierKey) || 0) + chargedQuantity);
          }
          state.remainingFreeUnits = Math.max(0, state.remainingFreeUnits - freeUnits);
          return state;
        }, {
          map: new Map<string, number>(),
          remainingFreeUnits: Number(freeLimit || 0),
        }).map;

        return items.map((item: any) => {
          const modifierAmount = getModifierChargeAmount(item);
          const quantity = getModifierQuantity(item);
          const apiUnitPrice = getModifierApiUnitPrice(item, modifierAmount);
          const apiQuantity = getModifierApiQuantity(item);
          const chargedQuantity = chargedUnitCounts.get(getModifierIdentity(item)) || 0;
          const chargedSubTotal = getModifierApiSubTotal(
            item,
            modifierAmount,
            chargedQuantity,
          );
          const hasFreeQuantity = quantity > chargedQuantity;

          return {
            ...item,
            legacyQuantity: quantity,
            quantity: apiQuantity,
            newDefaultTopping:
              !item?.defaultTopping && item?.selected && hasFreeQuantity ? 1 : 0,
            unitPrice: apiUnitPrice,
            subTotal: chargedSubTotal,
            totalValue: chargedSubTotal,
            isFree: chargedQuantity > 0 ? 0 : 1,
            pizzaRegion: getModifierRegion(item),
            PizzaRegion: getModifierRegion(item),
            isMultiplier: quantity > 1 ? 1 : 0,
            Multiplier: quantity,
            multiplier: quantity,
          };
        });
      });
    },
    [
      getFreeToppingsPerPizzaCount,
      getModifierChargeAmount,
      getModifierIdentity,
      getSelectedModifierCategories,
      selectedProduct,
    ],
  );

  const getCombinedDealPricedModifiersBySlot = useCallback(
    (pizzaConfigs: any[] = [], sizeItem?: any) => {
      const totalFreeLimit = getTotalFreeToppingCount();
      const extraToppingUnitPrice = getExtraToppingUnitPrice(sizeItem);
      const tierPrices = Object.entries(sizeItem || {})
        .map(([key, value]: any) => {
          const match = /^toppingPrice(\d+)$/i.exec(key);
          return match && Number(value || 0) > 0
            ? { count: Number(match[1]), price: Number(value) }
            : null;
        })
        .filter(Boolean)
        .sort((a: any, b: any) => a.count - b.count) as {
        count: number;
        price: number;
      }[];
      const hasToppingPriceTiers = tierPrices.length > 0;
      const shouldUseCombinedFree =
        Number(selectedProduct?.isCreateYourOwn || 0) === 1 &&
        getDealPizzaCount(selectedProduct) > 1 &&
        (totalFreeLimit > 0 || hasToppingPriceTiers);

      if (!shouldUseCombinedFree) {
        return pizzaConfigs.map((pizzaConfig: any) =>
          getPricedSelectedModifiers(pizzaConfig?.modifications),
        );
      }

      const selectedBySlot = pizzaConfigs.map((pizzaConfig: any) =>
        getSelectedModifierCategories(pizzaConfig?.modifications).flatMap(({ items }: any) => items),
      );
      const selectedTierCount = Math.max(
        0,
        ...selectedBySlot.map((items: any[] = []) =>
          getPricedSelectedModifierUnitCountFromItems(items),
        ),
      );
      const selectedTier =
        tierPrices.find((tier) => tier.count >= selectedTierCount) ||
        tierPrices[tierPrices.length - 1];
      const includedToppingsPerPizza = Math.max(0, Number(selectedTier?.count || 0));

      const toChargeableUnits = (items: any[] = [], slotIndex: number) =>
        items
          .filter(
            (item: any) => getModifierChargeAmount(item, extraToppingUnitPrice) > 0,
          )
          .map((item: any, orderIndex: number) => ({
            item,
            slotIndex,
            orderIndex,
            selectionUnits: getModifierSelectionUnitCount(item),
            chargeSelectionUnits: getModifierQuantity(item) * getModifierSideFactor(item),
            legacyQuantity: getModifierQuantity(item),
            sideFactor: getModifierSideFactor(item),
          }));

      const chargedUnitCounts =
        totalFreeLimit > 0
          ? (() => {
              const defaultUnits = selectedBySlot.flatMap((items, slotIndex) =>
                toChargeableUnits(
                  items.filter((item: any) => Number(item?.defaultTopping || 0) === 1),
                  slotIndex,
                ),
              );
              const extraUnits = selectedBySlot
                .flatMap((items, slotIndex) =>
                  toChargeableUnits(
                    items.filter((item: any) => Number(item?.defaultTopping || 0) !== 1),
                    slotIndex,
                  ),
                )
                .sort(
                  (a: any, b: any) =>
                    getModifierChargeAmount(a.item, extraToppingUnitPrice) -
                      getModifierChargeAmount(b.item, extraToppingUnitPrice) ||
                    a.slotIndex - b.slotIndex ||
                    a.orderIndex - b.orderIndex,
                );

              return [...defaultUnits, ...extraUnits].reduce((state: any, unit: any) => {
                const chargeSelectionUnits = Number(unit.chargeSelectionUnits ?? unit.selectionUnits);
                const isDefaultTopping = Number(unit.item?.defaultTopping || 0) === 1;
                const freeUnits = isDefaultTopping
                  ? Math.min(chargeSelectionUnits, unit.sideFactor)
                  : getFreeSelectionUnitsForChargeableUnit(
                      state.remainingFreeUnits,
                      unit.selectionUnits,
                      unit.sideFactor,
                    );
                const chargedSelectionUnits = Math.max(0, chargeSelectionUnits - freeUnits);
                const chargedQuantity =
                  unit.sideFactor > 0 ? chargedSelectionUnits / unit.sideFactor : 0;
                const key = `${unit.slotIndex}:${getModifierIdentity(unit.item)}`;
                if (chargedQuantity > 0) {
                  state.map.set(key, (state.map.get(key) || 0) + chargedQuantity);
                }
                state.remainingFreeUnits = Math.max(0, state.remainingFreeUnits - freeUnits);
                return state;
              }, {
                map: new Map<string, number>(),
                remainingFreeUnits: totalFreeLimit,
              }).map;
            })()
          : selectedBySlot.reduce((map: Map<string, number>, items, slotIndex) => {
        const defaultUnits = toChargeableUnits(
          items.filter((item: any) => Number(item?.defaultTopping || 0) === 1),
          slotIndex,
        );
        const extraUnits = toChargeableUnits(
          items.filter((item: any) => Number(item?.defaultTopping || 0) !== 1),
          slotIndex,
        ).sort(
          (a: any, b: any) =>
            getModifierChargeAmount(a.item, extraToppingUnitPrice) -
              getModifierChargeAmount(b.item, extraToppingUnitPrice) ||
            a.orderIndex - b.orderIndex,
        );
        let remainingFreeUnits = includedToppingsPerPizza || totalFreeLimit;

        [...defaultUnits, ...extraUnits].forEach((unit: any) => {
          const chargeSelectionUnits = Number(unit.chargeSelectionUnits ?? unit.selectionUnits);
          const isDefaultTopping = Number(unit.item?.defaultTopping || 0) === 1;
          const freeUnits = isDefaultTopping
            ? Math.min(chargeSelectionUnits, unit.sideFactor)
            : getFreeSelectionUnitsForChargeableUnit(
                remainingFreeUnits,
                unit.selectionUnits,
                unit.sideFactor,
              );
          const chargedSelectionUnits = Math.max(0, chargeSelectionUnits - freeUnits);
          const chargedQuantity =
            unit.sideFactor > 0 ? chargedSelectionUnits / unit.sideFactor : 0;
          const key = `${unit.slotIndex}:${getModifierIdentity(unit.item)}`;
          if (chargedQuantity > 0) {
            map.set(key, (map.get(key) || 0) + chargedQuantity);
          }
          remainingFreeUnits = Math.max(0, remainingFreeUnits - freeUnits);
        });

        return map;
      }, new Map<string, number>());

      return selectedBySlot.map((items, slotIndex) =>
        items.map((item: any) => {
          const modifierAmount = getModifierChargeAmount(item, extraToppingUnitPrice);
          const quantity = getModifierQuantity(item);
          const apiUnitPrice = getModifierApiUnitPrice(item, modifierAmount);
          const apiQuantity = getModifierApiQuantity(item);
          const chargedQuantity =
            chargedUnitCounts.get(`${slotIndex}:${getModifierIdentity(item)}`) || 0;
          const chargedSubTotal = getModifierApiSubTotal(
            item,
            modifierAmount,
            chargedQuantity,
          );
          const hasFreeQuantity = quantity > chargedQuantity;

          return {
            ...item,
            legacyQuantity: quantity,
            quantity: apiQuantity,
            newDefaultTopping:
              !item?.defaultTopping && item?.selected && hasFreeQuantity ? 1 : 0,
            unitPrice: apiUnitPrice,
            subTotal: chargedSubTotal,
            totalValue: chargedSubTotal,
            isFree: chargedQuantity > 0 ? 0 : 1,
            pizzaRegion: getModifierRegion(item),
            PizzaRegion: getModifierRegion(item),
            isMultiplier: quantity > 1 ? 1 : 0,
            Multiplier: quantity,
            multiplier: quantity,
          };
        }),
      );
    },
    [
      getPricedSelectedModifiers,
      getExtraToppingUnitPrice,
      getModifierChargeAmount,
      getModifierIdentity,
      getSelectedModifierCategories,
      getPricedSelectedModifierUnitCountFromItems,
      getTotalFreeToppingCount,
      selectedProduct,
    ],
  );
  const getPricedSelectedModifierUnitCount = useCallback(
    (modifications: any) =>
      getSelectedModifierCategories(modifications).reduce(
        (sum: number, { items }: any) =>
          sum + getPricedSelectedModifierUnitCountFromItems(items),
        0,
      ),
    [getPricedSelectedModifierUnitCountFromItems, getSelectedModifierCategories],
  );

  const getRemovedDefaultToppingLines = useCallback((modifications: any) => {
    if (!modifications || typeof modifications !== "object") return [];

    return Object.values(modifications)
      .flat()
      .filter(
        (item: any) => Number(item?.defaultTopping) === 1 && item?.selected === false,
      )
      .map((item: any) => `** No - ${String(item?.pizzaModifierName || "").toUpperCase()}`)
      .filter(Boolean);
  }, []);

  const buildPizzaNote = useCallback(
    (
      pizzaConfig: any,
      baseNote?: any,
      overrides?: { includeAutoLines?: boolean },
    ) => {
      const autoLines =
        overrides?.includeAutoLines === false
          ? []
          : getRemovedDefaultToppingLines(pizzaConfig?.modifications);
      const userNote = stripAutoPizzaNoteLines(baseNote ?? pizzaConfig?.note ?? "");

      return [...autoLines, userNote].filter(Boolean).join("\n");
    },
    [getRemovedDefaultToppingLines],
  );

  const getCombinedPizzaNote = useCallback(
    (pizzaConfig: any) => {
      return buildPizzaNote(pizzaConfig);
    },
    [buildPizzaNote],
  );

  const allVariants = useMemo(
    () =>
      variants.reduce((obj: any, { data = [] }: any) => {
        data.forEach((element: any) => {
          const elementId =
            element?.id ?? element?.variantId ?? element?.itemId ?? element?.productId ?? element?.value;
          if (!elementId && elementId !== 0) return;
          obj[elementId] = element;
          element?.subLevelData?.forEach((subElement: any) => {
            const subElementId =
              subElement?.id ??
              subElement?.variantId ??
              subElement?.itemId ??
              subElement?.productId ??
              subElement?.value;
            if (subElementId || subElementId === 0) obj[subElementId] = subElement;
          });
        });
        return obj;
      }, {}),
    [variants]
  );

  const getSelectedVariantsForSubmit = useCallback(
    (variantConfig: any[] = []) => {
      const tempVariants: any[] = [];
      let total = 0;

      if (!Array.isArray(variantConfig)) return { selectedItemsLevel: tempVariants, total };

      variantConfig.forEach((item, levelIndex) => {
        Object.entries(item || {}).forEach(([key, value]: any) => {
          if (allVariants?.[key]) {
            const selectedVariant = allVariants[key];
            const price = Number(selectedVariant?.price || 0);
            total += price;
            tempVariants.push({
              ...selectedVariant,
              id: Number(selectedVariant?.id || key),
              name: selectedVariant?.name || "",
              price,
              quantity: Number(selectedVariant?.quantity || 1),
              level: Number(selectedVariant?.level ?? levelIndex + 1),
              isMultiplier: 0,
              Multiplier: 1,
              PizzaRegion: 0,
              isSubLevel: 0,
            });
          }
          Array.from(value || []).forEach((subItem: any) => {
            if (typeof subItem === "object" && subItem !== null) return;
            if (allVariants?.[subItem]) {
              const selectedSubVariant = allVariants[subItem];
              const price = Number(selectedSubVariant?.price || 0);
              total += price;
              tempVariants.push({
                ...selectedSubVariant,
                id: Number(selectedSubVariant?.id || subItem),
                name: selectedSubVariant?.name || "",
                price,
                quantity: Number(selectedSubVariant?.quantity || 1),
                level: Number(selectedSubVariant?.level ?? levelIndex + 1),
                isMultiplier: 0,
                Multiplier: 1,
                PizzaRegion: 0,
                isSubLevel: 1,
              });
            }
          });
        });
      });

      return { selectedItemsLevel: tempVariants, total };
    },
    [allVariants],
  );

  const totalModifications = useMemo(
    () => getPricedSelectedModifierUnitCount(config?.modifications),
    [config?.modifications, getPricedSelectedModifierUnitCount],
  );

  const getSelectedModifierCount = useCallback(
    (modifications: any) => getPricedSelectedModifierUnitCount(modifications),
    [getPricedSelectedModifierUnitCount],
  );

  const hasAnySelectedModifier = useCallback(
    (modifications: any) => getSelectedModifierCount(modifications) > 0,
    [getSelectedModifierCount],
  );

  const combinedDealToppingSummary = useMemo(() => {
    const combinedLimit = getTotalFreeToppingCount();
    if (!selectedProduct?.isCreateYourOwn || combinedLimit <= 0) {
      return {
        combinedLimit: 0,
        selectedCount: 0,
        remainingCount: 0,
      };
    }

    const sourcePizzas = hasDealPizzaSlots(selectedProduct)
      ? (() => {
          const next = [...pizzasInDeal];
          next[currentPizzaIndex] = config;
          return next;
        })()
      : [config];
    const selectedCount = sourcePizzas.reduce(
      (sum: number, pizzaConfig: any) =>
        sum + getSelectedModifierCount(pizzaConfig?.modifications),
      0,
    );

    return {
      combinedLimit,
      selectedCount,
      remainingCount: Math.max(0, combinedLimit - selectedCount),
    };
  }, [
    config,
    currentPizzaIndex,
    getSelectedModifierCount,
    getTotalFreeToppingCount,
    pizzasInDeal,
    selectedProduct,
  ]);

  const getChargeableCreateYourOwnToppingCount = useCallback(
    (modifications: any) =>
      Math.max(
        0,
        getSelectedModifierCount(modifications) - getFreeToppingsPerPizzaCount(),
      ),
    [getFreeToppingsPerPizzaCount, getSelectedModifierCount],
  );

  const getCreateYourOwnBaseTierCount = useCallback(() => {
    if (
      Number(selectedProduct?.isPizza || 0) === 1 &&
      Number(selectedProduct?.isCreateYourOwn || 0) === 1
    ) {
      return getFreeToppingsPerPizzaCount();
    }

    return 0;
  }, [getFreeToppingsPerPizzaCount, selectedProduct]);

  const autoRemovedToppingNotes = useMemo(
    () => getRemovedDefaultToppingLines(config?.modifications),
    [config?.modifications, getRemovedDefaultToppingLines],
  );

  const autoToppingSummaryNote = useMemo(
    () => buildToppingSummaryNoteLine(config?.modifications),
    [config?.modifications],
  );

  const toppingSummary = useMemo(() => {
    const selectedToppings = getPricedSelectedModifiers(config?.modifications);
    const defaults = buildToppingDisplayGroups(
      selectedToppings.filter((item: any) => Number(item?.defaultTopping) === 1),
    );
    const extras = buildToppingDisplayGroups(
      selectedToppings.filter((item: any) => Number(item?.defaultTopping) !== 1),
    );

    return { defaults, extras };
  }, [config?.modifications, getPricedSelectedModifiers]);

  const hasDefaultToppingSummary = useMemo(
    () =>
      toppingSummary.defaults.whole.length > 0 ||
      toppingSummary.defaults.left.length > 0 ||
      toppingSummary.defaults.right.length > 0,
    [toppingSummary],
  );

  const hasExtraToppingSummary = useMemo(
    () =>
      toppingSummary.extras.whole.length > 0 ||
      toppingSummary.extras.left.length > 0 ||
      toppingSummary.extras.right.length > 0,
    [toppingSummary],
  );

  const hasTouchedToppings = useMemo(() => {
    if (!config?.modifications || typeof config.modifications !== "object") {
      return false;
    }

    return Object.values(config.modifications)
      .flat()
      .some((item: any) => {
        const isDefault = Number(item?.defaultTopping) === 1;
        const isSelected = item?.selected !== false;
        return isSelected !== isDefault;
      });
  }, [config?.modifications]);

  const popupDefaultToppings = useMemo(
    () =>
      (selectedProduct?.defaultPizzaToppings || [])
        .filter((item: any) => item?.selected !== false)
        .map((item: any) => toTitleCase(item?.modifierName || item?.pizzaModifierName))
        .filter(Boolean),
    [selectedProduct],
  );

  useEffect(() => {
    const id = product?.productId;
    if (id && product.isPizza) {
      api
        .get("/api/Modifier/PizzaSubLevels?productId=" + id)
        .then(({ data }) => {
          setSpiceData(data);
        })
        .catch((e) => {});
    }
  }, [product]);

  useEffect(() => {
    if (!selectedProduct?.isPizza || !spiceData.length) return;

    const firstSpice = spiceData[0];
    const firstSpiceName = String(firstSpice?.modifierName || "").trim();
    if (!firstSpiceName) return;

    const firstSpiceLevel = {
      spiceId: firstSpice?.modifierId || 1,
      spicename: firstSpiceName,
    };

    setConfig((prev: any) => {
      if (String(prev?.spiceLevel || "").trim()) return prev;

      const next = {
        ...prev,
        spiceLevel: firstSpiceName,
        existingSpiceLevel: firstSpiceLevel,
      };
      configRef.current = next;
      return next;
    });

    if (hasDealPizzaSlots(selectedProduct)) {
      setPizzasInDeal((prev) => {
        let changed = false;
        const next = prev.map((pizza: any) => {
          if (String(pizza?.spiceLevel || "").trim()) return pizza;
          changed = true;
          return clonePizzaConfig({
            ...pizza,
            spiceLevel: firstSpiceName,
            existingSpiceLevel: firstSpiceLevel,
          });
        });

        if (changed) pizzasInDealRef.current = next;
        return changed ? next : prev;
      });
    }
  }, [clonePizzaConfig, selectedProduct, spiceData]);

  useEffect(() => {
    return () => {
      if (cartOpenTimeoutRef.current) {
        window.clearTimeout(cartOpenTimeoutRef.current);
      }
      dispatch(setCartBtnLoading(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!product?.data) return;

    if (hasDealPizzaSlots(product.data)) {
      const cartonfig =
        pizzasInDeal?.length > 0
          ? {
              ...pizzasInDeal?.[currentPizzaIndex],
            }
          : {
              ...mapDealPizzaToConfig(
                product.data?.selectedDealPizzas?.[currentPizzaIndex]
              ),
            };

      const indexFind = checkCartConfigData.findIndex(
        (e) => e === currentPizzaIndex
      );

      if (indexFind === -1) {
        setConfig({ ...cartonfig });
        setCheckCartConfigData((e) => [...e, currentPizzaIndex]);
      }
    }
  }, [checkCartConfigData, currentPizzaIndex, pizzasInDeal, product?.data]);

  useEffect(() => {
    if (product?.data) {
      setSelectedProduct(product.data);

      if (hasDealPizzaSlots(product.data)) {
        const normalizedDealPizzas = (product.data?.selectedDealPizzas || []).map(
          (dealPizza: any) => mapDealPizzaToConfig(dealPizza)
        );
        debugDealEdit("OPEN_EDIT_DEAL", {
          productId: product?.data?.productId,
          pizzasInDeal: product?.data?.pizzasInDeal,
          selectedDealPizzas: product?.data?.selectedDealPizzas,
          normalizedDealPizzas,
        });
        setCurrentPizzaIndex(0);
        setCheckCartConfigData([0]);
        setPizzasInDeal(normalizedDealPizzas.map((pizza: any) => clonePizzaConfig(pizza)));
        setConfig(clonePizzaConfig(normalizedDealPizzas[0]));
        const savedDealVariants = Array.isArray(product.data?.config?.variant)
          ? product.data.config.variant
          : normalizedDealPizzas.find((pizza: any) => Array.isArray(pizza?.variant) && pizza.variant.length)
              ?.variant || [];
        setDealVariantConfig(safeClone(savedDealVariants));
      } else {
        setCurrentPizzaIndex(0);
        setCheckCartConfigData([0]);
        setDealVariantConfig([]);
        setConfig({
          ...product.data.config,
          note: stripAutoPizzaNoteLines(
            preferNonEmptyString(product.data.config?.note, (product as any)?.note, ""),
          ),
        });
      }
    } else {
      setSelectedProduct(product);
      if (hasDealPizzaSlots(product)) {
        const initialPizzas = Array.from(
          { length: getDealPizzaCount(product) },
          () => createInitialPizzaConfig()
        );
        setCurrentPizzaIndex(0);
        setCheckCartConfigData([0]);
        setPizzasInDeal(initialPizzas.map((pizza: any) => clonePizzaConfig(pizza)));
        setConfig(clonePizzaConfig(initialPizzas[0]));
        setDealVariantConfig([]);
      } else {
        setDealVariantConfig([]);
      }
    }
  }, [clonePizzaConfig, product, safeClone]);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    pizzasInDealRef.current = pizzasInDeal;
  }, [pizzasInDeal]);

  useEffect(() => {
    if (!hasDealPizzaSlots(selectedProduct) || selectedProduct?.isCreateYourOwn) {
      setDealPizzaOptions([]);
      dealPizzaFetchKeyRef.current = "";
      return;
    }

    const cachedOptions = mergePizzaProducts([], pizzaList);
    setDealPizzaOptions(cachedOptions);

    const pizzaCategoryIds = (sideBarMenu || [])
      .filter((category: any) => {
        const label = String(category?.categoryDescription || "").toLowerCase();
        return label.includes("pizza") && !label.includes("create your own");
      })
      .map((category: any) => Number(category?.id || 0))
      .filter(Boolean);
    const fetchKey = `${selectedProduct?.productId || 0}|${pizzaCategoryIds.join(",")}`;
    if (!pizzaCategoryIds.length || dealPizzaFetchKeyRef.current === fetchKey) return;

    let cancelled = false;
    dealPizzaFetchKeyRef.current = fetchKey;
    setDealPizzaOptionsLoading(true);

    Promise.all(
      pizzaCategoryIds.map((categoryId: number) =>
        api
          .get(`/api/Product/ByCategoryId?categoryId=${categoryId}`)
          .then(({ data }) => (Array.isArray(data) ? data : []))
          .catch(() => [])
      )
    )
      .then((results) => {
        if (cancelled) return;
        const fetchedOptions = mergePizzaProducts(
          cachedOptions,
          results.flat()
        );
        setDealPizzaOptions(fetchedOptions);
        dispatch(setPizzaList(mergePizzaProducts(pizzaList, fetchedOptions)));
      })
      .finally(() => {
        if (!cancelled) setDealPizzaOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, pizzaList, selectedProduct, sideBarMenu]);

  useEffect(() => {
      if (selectedProduct) {
        let ignore = false;

      const variantProductId =
        selectedProduct.isDeal && selectedProduct.isPizza
          ? selectedProduct.productId
          : selectedProduct.isPizza
          ? activePizzaItemProductId
          : selectedProduct.productId;

      if (variantProductId) {
        api
          .get(`/api/Variation/VariantById?productId=${variantProductId}`)
          .then(({ data }) => {
            if (ignore) return;
            setVariants(data);
          })
          .catch(() => {
            if (!ignore) setVariants([]);
          });
      } else {
        setVariants([]);
      }

      if (selectedProduct.isPizza) {
        const shouldAutoSelectPizzaDefaults =
          !selectedProduct.isDeal || !!config.id || !!product?.isEdit;

        const productId =
          selectedProduct.isDeal && !selectedProduct.isCreateYourOwn
            ? activeDealBaseProductId
            : activePizzaItemProductId;
        if (!productId) return;

        api
          .get(
            `/api/${
              selectedProduct.isCreateYourOwn
                ? "PizzaToppingPrice"
                : "PizzaSize/ByProductId"
            }?productId=${productId}`
          )
          .then(({ data }) => {
            if (ignore) return;
            const cleanSizes = normalizePizzaSizes(data || []);
            setSizes(cleanSizes);
            if (shouldAutoSelectPizzaDefaults) {
              const sharedSizeForDeal =
                selectedProduct?.isDeal && currentPizzaIndex > 0
                  ? Number(pizzasInDealRef.current?.[0]?.size || 0)
                  : 0;
              const nextAutoSize = cleanSizes.some(
                (item: any) => Number(item?.sizeId) === Number(sharedSizeForDeal),
              )
                ? sharedSizeForDeal
                : cleanSizes.some((item: any) => Number(item?.sizeId) === Number(config.size))
                ? config.size
                : cleanSizes[0]?.sizeId || 0;
              const nextAutoSizeObj = cleanSizes.find(
                (item: any) => Number(item?.sizeId) === Number(nextAutoSize),
              );
              const nextExistingPizzaSize = nextAutoSizeObj
                ? {
                    pizzasizeId: nextAutoSizeObj.sizeId,
                    size: nextAutoSizeObj.sizeName,
                    sizePriceX1: Number(nextAutoSizeObj?.sizePriceX1 || 0),
                    sizePriceX2: Number(nextAutoSizeObj?.sizePriceX2 || 0),
                    sizePriceX3: Number(nextAutoSizeObj?.sizePriceX3 || 0),
                    sizePriceX4: Number(nextAutoSizeObj?.sizePriceX4 || 0),
                    sizePriceX5: Number(nextAutoSizeObj?.sizePriceX5 || 0),
                  }
                : null;

              if (hasDealPizzaSlots(selectedProduct) && nextAutoSize) {
                setPizzasInDeal((prev) => {
                  const next = prev.map((pizza: any) =>
                    clonePizzaConfig({
                      ...pizza,
                      size: nextAutoSize,
                      existingPizzaSize: nextExistingPizzaSize,
                      existingSizePrice: Number(
                        getSizePrice(nextAutoSizeObj, pizza?.modifications) || 0,
                      ),
                    }),
                  );
                  pizzasInDealRef.current = next;
                  return next;
                });
              }

              setConfig((e) => ({
                ...e,
                size: nextAutoSize,
                existingPizzaSize: nextExistingPizzaSize,
                existingSizePrice: Number(getSizePrice(nextAutoSizeObj, e?.modifications) || 0),
              }));
            }
          })
          .catch(() => {});
      }

      return () => {
        ignore = true;
      };
    }
  }, [selectedProduct, product, config.id, activeDealBaseProductId, activePizzaItemProductId, currentPizzaIndex]);

  useEffect(() => {
    if (selectedProduct && selectedProduct.isPizza && config.size) {
      let ignore = false;

      const productId =
        selectedProduct.isDeal && !selectedProduct.isCreateYourOwn
          ? activeDealBaseProductId
          : activePizzaItemProductId;
      if (!productId) return;

      api
        .get(
          `/api/Modifier/Crusts?sizeId=${config.size}&crustId=0&productId=${productId}`
        )
        .then(({ data }) => {
          if (ignore) return;
          setCrusts(data);
          updateCurrentPizzaConfig((prev: any) => {
            const selectedCrust =
              data.find(
                (item: any) => Number(item?.modifierId) === Number(prev.crust),
              ) ||
              data.find(
                (item: any) =>
                  Number(item?.modifierId) ===
                  Number(prev.existingPizzaCrust?.crustId),
              ) ||
              data[0];

            return {
              ...prev,
              crust: Number(selectedCrust?.modifierId || 0),
              existingPizzaCrust: selectedCrust
                ? {
                    crustId: selectedCrust.modifierId,
                    crustName: selectedCrust.modifierName,
                    crustPrice: selectedCrust.modifierCost,
                  }
                : null,
            };
          });
        })
        .catch((error) => {
          console.error("Failed to load crusts", error);
          showApiErrorToast(error, "Unable to load pizza crusts");
        });

      return () => {
        ignore = true;
      };
    }
  }, [
    config.size,
    selectedProduct,
    product,
    config.id,
    activeDealBaseProductId,
    activePizzaItemProductId,
    updateCurrentPizzaConfig,
  ]);

  useEffect(() => {
    if (!selectedProduct?.isPizza || !config.size) return;
    let ignore = false;
    const savedCartModifiers = (config as any)?.savedCartModifiers;
    const shouldRestoreSavedModifiers =
      !!product?.isEdit &&
      Array.isArray(savedCartModifiers) &&
      savedCartModifiers.length > 0;

    if (config.modifications && !shouldRestoreSavedModifiers) return;

    const productId = activePizzaItemProductId;
    if (!productId) return;
    const modifierLoadKey = [
      currentPizzaIndex,
      productId,
      config.size,
      Number(shouldRestoreSavedModifiers),
      Array.isArray(savedCartModifiers) ? savedCartModifiers.length : 0,
    ].join(":");

    if (modifierLoadKeyRef.current === modifierLoadKey) {
      return;
    }

    modifierLoadKeyRef.current = modifierLoadKey;

    api
      .get(
        `/api/Modifier/ModifierCategories?${new URLSearchParams({
          productId: String(productId),
          pizzaSizeId: String(config.size),
          sizeId: String(config.size),
        })}`,
      )
      .then(({ data }) => {
        if (ignore) return;
        if (!Array.isArray(data) || !data.length) return;

        const nextModifications =
          shouldRestoreSavedModifiers
            ? buildSavedModifierConfig(data, config as any)
            : buildDefaultModifierConfig(data);

        debugDealEdit("RESTORE_MODIFIERS", {
          currentPizzaIndex,
          shouldRestoreSavedModifiers,
          configBefore: config,
          nextModifications,
          categories: data,
        });

        setConfig((prev: any) => {
          if (prev.modifications && !shouldRestoreSavedModifiers) return prev;
          return {
            ...prev,
            modifications: nextModifications,
            savedCartModifiers: prev.savedCartModifiers,
          };
        });

        if (hasDealPizzaSlots(selectedProduct)) {
          setPizzasInDeal((prev) => {
            const next = [...prev];
            const current = next[currentPizzaIndex] || createInitialPizzaConfig();
            if (!current.modifications || shouldRestoreSavedModifiers) {
              next[currentPizzaIndex] = clonePizzaConfig({
                ...current,
                modifications: nextModifications,
                savedCartModifiers: current.savedCartModifiers,
              });
            }
            return next;
          });
        }
      })
      .catch(() => {});

    return () => {
      ignore = true;
    };
  }, [
    clonePizzaConfig,
    activePizzaItemProductId,
    product?.isEdit,
    config.id,
    config.size,
    currentPizzaIndex,
    selectedProduct,
  ]);

  const addItemToCart = () => {
    // (NO CHANGES INSIDE - keep your working logic)
    const updatedPizzasInDeal = [...pizzasInDealRef.current];
    updatedPizzasInDeal[currentPizzaIndex] = clonePizzaConfig(configRef.current);
    debugDealEdit("ADD_TO_CART_START", {
      currentPizzaIndex,
      config: configRef.current,
      pizzasInDeal: pizzasInDealRef.current,
      updatedPizzasInDeal,
    });
    const validDealPizzas = updatedPizzasInDeal.filter(
      (pizzaConfig: any) =>
        !!pizzaConfig &&
        (!!pizzaConfig.id ||
          !!pizzaConfig.pizzaName ||
          !!pizzaConfig.size ||
          !!pizzaConfig.crust ||
          !!pizzaConfig.modifications ||
          !!pizzaConfig.note)
    );
    toast.dismiss();
    const [tax1 = { rate: 0 }, tax2 = { rate: 0 }, tax3 = { rate: 0 }] =
      taxes ?? [];
    const activeConfig = configRef.current;
    const pizza = pizzaDropdownOptions.find((i) => i.productId === activeConfig.id);
    const size = sizes.find(
      (i) => Number(i.sizeId) === Number(activeConfig.size),
    );
    const crust = crusts.find(
      (i) => Number(i.modifierId) === Number(activeConfig.crust),
    );

    if (!selectedProduct) return;
    const availability = getProductOrderTypeAvailability(
      selectedProduct,
      activeOrderType
    );
    if (availability.unavailable) {
      toast.error(
        `${selectedProduct.productName || "This product"} is ${availability.reason.toLowerCase()}.`
      );
      return;
    }

    const isDeal = selectedProduct.isDeal;
    const isPizza = selectedProduct.isPizza;
    const isCreateYourOwn = selectedProduct.isCreateYourOwn;
    const isPizzasInDeal = hasDealPizzaSlots(selectedProduct);

    if (!areAllVariantLevelsValid || pizzaRequirementMessages.length > 0) {
      return;
    }

    const basePrice =
      settings.ApplyTakeOutDeliveryPrice !== 0
        ? deliveryType !== "pickup"
          ? selectedProduct?.deliveryprice || selectedProduct.price
          : selectedProduct?.takeoutprice || selectedProduct.price
        : !!selectedProduct.isDeal || !!selectedProduct.isCreateYourOwn
        ? 0
        : selectedProduct.price;

    const finalProduct: any = {
      data: {},
      id: selectedProduct.productId,
      productId: selectedProduct.productId,
      prodcat:
        selectedProduct?.prodcat ??
        selectedProduct?.prodCat ??
        selectedProduct?.categoryId ??
        selectedProduct?.__categoryId ??
        0,
      productCode: selectedProduct.productCode,
      fullDescription: selectedProduct.productName,
      quantity: activeConfig.qty,
      price: basePrice,
      variationsPrice: 0,
      totalUnitPrice: basePrice,
      subTotal: basePrice * activeConfig.qty,
      itemTotal: 0,
      taxId1: selectedProduct.taxId1,
      taxId2: selectedProduct.taxId2,
      taxId3: selectedProduct.taxId3,
      disableonordertype: selectedProduct.disableonordertype,
      disableordtype: selectedProduct.disableordtype,
      note: getCombinedPizzaNote(activeConfig),
      totalTax1: 0,
      totalTax2: 0,
      totalTax3: 0,
      status: "new",
      modifiers: [],
      isDeal,
      isPizza,
      isCreateYourOwn,
      isServiceSub: hideServiceSubPizzaOptions ? 1 : 0,
      isHalfHalfNotAllowed: hideHalfHalfToppingOptions ? 1 : 0,
      taxRate1: tax1?.rate ?? 0,
      taxRate2: tax2?.rate ?? 0,
      taxRate3: tax3?.rate ?? 0,
    };

    if (product?.data) {
      finalProduct.data = {
        ...product.data,
        config: activeConfig,
        isEdit: true,
        index: product.index,
      };
    } else {
      finalProduct.data = { ...product, config: activeConfig };
    }

    if (isPizza || isDeal || isCreateYourOwn) {
      if (!size || (!hideServiceSubPizzaOptions && !crust)) {
        toast.error(
          hideServiceSubPizzaOptions ? "Size is required" : "Size and Crust are required",
        );
        return;
      }

      if (isCreateYourOwn && !hasAnySelectedModifier(activeConfig.modifications)) {
        return;
      }

      if (crust) {
        finalProduct.pizzaCrust = {
          crustId: crust.modifierId,
          crustName: crust.modifierName,
          crustPrice: crust.modifierCost,
        };

        if (crust.modifierCost) {
          finalProduct.itemTotal += crust.modifierCost;
        }
      }

      finalProduct.pizzaSize = {
        pizzasizeId: size.sizeId,
        size: size.sizeName,
        sizePriceX1: Number(size?.sizePriceX1 || 0),
        sizePriceX2: Number(size?.sizePriceX2 || 0),
        sizePriceX3: Number(size?.sizePriceX3 || 0),
        sizePriceX4: Number(size?.sizePriceX4 || 0),
        sizePriceX5: Number(size?.sizePriceX5 || 0),
      };
    }
    if (isDeal || isPizza) {
      finalProduct.spiceLevel = {
        spiceId: 1,
        spicename: activeConfig.spiceLevel,
      };
    }

    if (isPizzasInDeal) {
      finalProduct.note = "";
      finalProduct.data.config = {
        ...(finalProduct.data.config || activeConfig),
        note: "",
      };

      const firstDealPizzaSize = sizes.find(
        (item) => Number(item?.sizeId) === Number(validDealPizzas[0]?.size),
      );
      const pricedDealModifiersBySlot =
        getCombinedDealPricedModifiersBySlot(validDealPizzas, firstDealPizzaSize);
      const combinedDealSizePrice = getCreateYourOwnDealSizePrice(
        firstDealPizzaSize,
        validDealPizzas,
      );
      finalProduct.data["selectedDealPizzas"] = validDealPizzas.map(
        (pizzaConfig, pizzaIndex) => {
          const sourceDealPizza = pizzaConfig?.sourceDealPizza || {};
          const selectedDealPizzaProduct = pizzaDropdownOptions.find(
            (item) => Number(item?.productId) === Number(pizzaConfig?.id || sourceDealPizza?.productId),
          );
          const matchedPizzaSize =
            sizes.find((s) => Number(s.sizeId) === Number(pizzaConfig.size)) ||
            firstDealPizzaSize;
          const resolvedPizzaSize =
            pizzaConfig?.existingPizzaSize ||
            sourceDealPizza?.pizzaSize ||
            (matchedPizzaSize
              ? {
                  pizzasizeId: matchedPizzaSize?.sizeId,
                  size: matchedPizzaSize?.sizeName || pizzaConfig?.size,
                  sizePriceX1: Number(matchedPizzaSize?.sizePriceX1 || 0),
                  sizePriceX2: Number(matchedPizzaSize?.sizePriceX2 || 0),
                  sizePriceX3: Number(matchedPizzaSize?.sizePriceX3 || 0),
                  sizePriceX4: Number(matchedPizzaSize?.sizePriceX4 || 0),
                  sizePriceX5: Number(matchedPizzaSize?.sizePriceX5 || 0),
                }
              : null);
          const resolvedSpiceName = pizzaConfig?.spiceLevel || activeConfig?.spiceLevel || "";
          const foundSpice = spiceData.find(
            (el) => el.modifierName === resolvedSpiceName
          );
          const resolvedSpiceLevel = foundSpice
            ? {
                spiceId: foundSpice?.modifierId || 1,
                spicename: foundSpice?.modifierName || resolvedSpiceName,
              }
            : pizzaConfig?.existingSpiceLevel ||
              sourceDealPizza?.spiceLevel || {
                spiceId: 1,
                spicename: resolvedSpiceName,
              };
          const savedCartModifiers = Array.isArray((pizzaConfig as any)?.savedCartModifiers)
            ? (pizzaConfig as any).savedCartModifiers
            : [];
          const hasSavedCartModifiers = savedCartModifiers.length > 0;
          const shouldUseLiveModifications = !!pizzaConfig?.modifications;
          const pizzaItemNumber = pizzaIndex + 1;
          const pricedDealSlotModifiers: any[] = shouldUseLiveModifications
            ? pizzaConfig?.modifications
            ? pricedDealModifiersBySlot[pizzaIndex] || []
            : []
            : hasSavedCartModifiers
            ? mapSavedCartModifiersForSubmit(savedCartModifiers, pizzaItemNumber)
            : [];
          const combinedPizzaNote = !shouldUseLiveModifications && hasSavedCartModifiers
            ? buildPizzaNote(
                pizzaConfig,
                preferNonEmptyString(
                  pizzaConfig?.existingFullNote,
                  sourceDealPizza?.note,
                  pizzaConfig?.note,
                  "",
                ),
                {
                  includeAutoLines: false,
                },
              )
            : pizzaConfig?.modifications
            ? getCombinedPizzaNote(pizzaConfig)
            : buildPizzaNote(pizzaConfig, pizzaConfig?.note || "", {
                includeAutoLines: false,
              });
          const modifierSubtotal = pricedDealSlotModifiers.reduce(
            (sum: number, item: any) => sum + getModifierLineAmount(item),
            0
          );
          const pricedModifiers: any[] = pricedDealSlotModifiers.map((item: any) =>
            mapDealPizzaModifierForSubmit(item, pizzaItemNumber),
          );
          const resolvedSizeSubtotal =
            (combinedDealSizePrice > 0 && pizzaIndex === 0 ? combinedDealSizePrice : 0) ||
            Number(pizzaConfig?.existingSizePrice || 0) ||
            (matchedPizzaSize ? +(getSizePrice(matchedPizzaSize, pizzaConfig?.modifications) || 0) : 0);
          const sizeSubtotal =
            getDealPizzaCount(selectedProduct) > 1
              ? pizzaIndex === 0
                ? resolvedSizeSubtotal
                : 0
              : resolvedSizeSubtotal;
          const sourcePizzaCrust =
            pizzaConfig?.existingPizzaCrust || sourceDealPizza?.pizzaCrust || null;
          const sourceCrustId = Number(
            sourcePizzaCrust?.crustId || sourcePizzaCrust?.modifierId || 0,
          );
          const sourceCrustName = String(
            sourcePizzaCrust?.crustName || sourcePizzaCrust?.modifierName || "",
          ).trim();
          const selectedCrustId = Number(
            pizzaConfig?.crust || sourceCrustId || activeConfig?.crust || 0,
          );
          const matchedCrust =
            crusts.find((item) => Number(item?.modifierId) === selectedCrustId) ||
            crusts.find((item) => Number(item?.modifierId) === sourceCrustId) ||
            crusts.find(
              (item) =>
                sourceCrustName &&
                String(item?.modifierName || "").trim().toLowerCase() ===
                  sourceCrustName.toLowerCase(),
            ) ||
            crust;
          const resolvedPizzaCrust =
            matchedCrust || sourcePizzaCrust
              ? {
                  crustId: Number(matchedCrust?.modifierId || sourceCrustId || 0),
                  crustName:
                    matchedCrust?.modifierName ||
                    sourceCrustName ||
                    String(sourcePizzaCrust?.crustName || ""),
                  crustPrice: Number(
                    sourcePizzaCrust?.crustPrice ?? matchedCrust?.modifierCost ?? 0,
                  ),
                }
              : null;
          const crustSubtotal = Number(
            pizzaConfig?.existingPizzaCrust?.crustPrice ||
              sourceDealPizza?.pizzaCrust?.crustPrice ||
              matchedCrust?.modifierCost ||
              0,
          );
          const parentDealSlotSubtotal =
            matchedPizzaSize || matchedCrust
              ? sizeSubtotal + crustSubtotal + modifierSubtotal
              : Number(
                  pizzaConfig?.existingSubTotal ||
                    sourceDealPizza?.subTotal ||
                    modifierSubtotal,
                );
          const subTotal = 0;
          const dealProductId = Number(selectedProduct?.productId || activeDealBaseProductId || 0);
          const dealPizzaProductId = Number(
            pizzaConfig?.id ||
              selectedDealPizzaProduct?.productId ||
              sourceDealPizza?.productId ||
              dealProductId,
          );
          const baseDealPizzaDescription = String(
            selectedDealPizzaProduct?.productName ||
              sourceDealPizza?.fullDescription ||
              sourceDealPizza?.pizzaName ||
              pizzaConfig?.pizzaName ||
              selectedProduct.productName,
          ).trim();
          const dealPizzaDescription = `${baseDealPizzaDescription || selectedProduct.productName} ${dealSlotLabel} ${pizzaItemNumber}`;
          const dealPizza = {
            id: dealPizzaProductId,
            crust: pizzaConfig.crust,
            config: clonePizzaConfig({
              ...pizzaConfig,
              pizzaName:
                pizzaConfig?.pizzaName ??
                selectedDealPizzaProduct?.productName ??
                sourceDealPizza?.pizzaName ??
                sourceDealPizza?.fullDescription ??
                selectedProduct?.productName,
              pizzaProductCode:
                pizzaConfig?.pizzaProductCode ??
                selectedDealPizzaProduct?.productCode ??
                sourceDealPizza?.productCode ??
                "",
              existingFullNote: combinedPizzaNote,
              existingPizzaSize: resolvedPizzaSize,
              existingSizePrice: resolvedSizeSubtotal,
              existingPizzaCrust: resolvedPizzaCrust,
              existingSpiceLevel: resolvedSpiceLevel,
              existingSubTotal: parentDealSlotSubtotal,
              dealProductId,
              pizzaIndex: pizzaItemNumber,
              PizzaNo: String(pizzaItemNumber),
              variant: [],
            }),
            dealProductId,
            pizzaIndex: pizzaItemNumber,
            PizzaNo: String(pizzaItemNumber),
            productId: dealPizzaProductId,
            productCode: String(
              pizzaConfig?.pizzaProductCode ||
              selectedDealPizzaProduct?.productCode ||
                sourceDealPizza?.productCode ||
                pizzaConfig?.pizzaName ||
                selectedProduct.productCode,
            ),
            fullDescription: dealPizzaDescription,
            description: dealPizzaDescription,
            quantity: 1,
            price: subTotal,
            variationsPrice: 0,
            totalUnitPrice: subTotal,
            subTotal,
            itemTotal: subTotal,
            taxId1: selectedProduct.taxId1,
            taxId2: selectedProduct.taxId2,
            taxId3: selectedProduct.taxId3,
            note: combinedPizzaNote,
            totalTax1: 0,
            totalTax2: 0,
            totalTax3: 0,
            status: "new",
            modifiers: pricedModifiers,
            isDeal: 1,
            isPizza: 1,
            isCreateYourOwn: 0,
            taxRate1: tax1?.rate ?? 0,
            taxRate2: tax2?.rate ?? 0,
            taxRate3: tax3?.rate ?? 0,
            spiceLevel: resolvedSpiceLevel,
            pizzaSize: resolvedPizzaSize,
            pizzaCrust: resolvedPizzaCrust,
            pizzaName:
              pizzaConfig.pizzaName ??
              selectedDealPizzaProduct?.productName ??
              sourceDealPizza?.pizzaName ??
              sourceDealPizza?.fullDescription ??
              selectedProduct?.productName,
          };

          if (selectedProduct.taxId1 === 1 && tax1?.rate) {
            const taxValue = +(parentDealSlotSubtotal * (tax1?.rate / 100)).toFixed(
              5
            );
            dealPizza.totalTax1 = taxValue;
          }
          if (selectedProduct.taxId2 === 2 && tax2?.rate) {
            const taxValue = +(parentDealSlotSubtotal * (tax2?.rate / 100)).toFixed(
              5
            );
            dealPizza.totalTax2 = taxValue;
          }
      if (selectedProduct.taxId3 === 3 && tax3?.rate) {
        const taxValue = +(parentDealSlotSubtotal * (tax3.rate / 100)).toFixed(
          5
        );
        dealPizza.totalTax3 = taxValue;
      }

          finalProduct.subTotal += parentDealSlotSubtotal * activeConfig.qty;
          return dealPizza;
        }
      );

      debugDealEdit("ADD_TO_CART_DEAL_PAYLOAD", {
        currentPizzaIndex,
        validDealPizzas,
        selectedDealPizzas: finalProduct.data["selectedDealPizzas"],
        finalProduct,
      });

      finalProduct.itemTotal = finalProduct.subTotal;
      finalProduct.totalTax1 = finalProduct.data.selectedDealPizzas.reduce(
        (sum: number, pizza: any) => sum + pizza.totalTax1,
        0
      );
      finalProduct.totalTax2 = finalProduct.data.selectedDealPizzas.reduce(
        (sum: number, pizza: any) => sum + pizza.totalTax2,
        0
      );
      finalProduct.totalTax3 = finalProduct.data.selectedDealPizzas.reduce(
        (sum: number, pizza: any) => sum + pizza.totalTax3,
        0
      );

      const selectedDealVariants = getSelectedVariantsForSubmit(dealVariantConfig);
      const dealVariantUnitTotal = selectedDealVariants.total;
      const dealVariantSubtotal = dealVariantUnitTotal * activeConfig.qty;
      finalProduct.variationsPrice = dealVariantUnitTotal;
      finalProduct.selectedItemsLevel = selectedDealVariants.selectedItemsLevel;
      finalProduct.data.config = {
        ...(finalProduct.data.config || activeConfig),
        variant: dealVariantConfig,
      };

      if (dealVariantSubtotal > 0) {
        finalProduct.subTotal += dealVariantSubtotal;
        if (selectedProduct.taxId1 === 1 && tax1?.rate) {
          finalProduct.totalTax1 += +((dealVariantSubtotal * tax1.rate) / 100).toFixed(5);
        }
        if (selectedProduct.taxId2 === 2 && tax2?.rate) {
          finalProduct.totalTax2 += +((dealVariantSubtotal * tax2.rate) / 100).toFixed(5);
        }
        if (selectedProduct.taxId3 === 3 && tax3?.rate) {
          finalProduct.totalTax3 += +((dealVariantSubtotal * tax3.rate) / 100).toFixed(5);
        }
      }

      finalProduct.itemTotal = finalProduct.subTotal;
      finalProduct.itemTotal +=
        finalProduct.totalTax1 +
        finalProduct.totalTax2 +
        finalProduct.totalTax3;
      finalProduct.totalUnitPrice =
        finalProduct.subTotal / Math.max(1, Number(activeConfig.qty || 1));
    } else {
      if (config?.modifications) {
        const allModifiers = getPricedSelectedModifiers(config.modifications);

        finalProduct.modifiers = allModifiers.map((item: any) => {
          return {
            defaultTopping: item.defaultTopping,
            newDefaultTopping: item.newDefaultTopping,
            modifierId: item.pizzaModifierId,
            description: item.pizzaModifierName,
            legacyQuantity: item.legacyQuantity,
            quantity: item.quantity,
            isMultiplier: getModifierIsMultiplier(item),
            Multiplier: getModifierMultiplier(item),
            multiplier: getModifierMultiplier(item),
            unitPrice: item.unitPrice,
            subTotal: item.subTotal,
            totalValue: item.totalValue,
            note: buildModifierNote(item),
            isFree: item.isFree,
            pizzaRegion: getModifierRegion(item),
            PizzaRegion: getModifierRegion(item),
          };
        });

        const toppingPrice = allModifiers.reduce(
          (sum: number, item: any) =>
            sum + getModifierLineAmount(item),
          0,
        );

        finalProduct.itemTotal += toppingPrice;
      } else {
        const defaultSelectedToppings = (selectedProduct.defaultPizzaToppings || []).map(
          (item: any) => ({
            ...item,
            pizzaModifierId: item.pizzaModifierId || item.modifierId,
            pizzaModifierName: item.pizzaModifierName || item.modifierName,
            pizzaModifierPrice: getModifierAmount(item),
            selected: item?.selected !== false,
          }),
        );
        const defaultSelectedToppingPrice = defaultSelectedToppings.reduce(
          (sum: number, item: any) =>
            item?.selected !== false ? sum + getModifierAmount(item) : sum,
          0,
        );

        finalProduct.modifiers = defaultSelectedToppings
          .filter((item: any) => item?.selected !== false)
          .map((item: any) => {
            const modifierAmount = getModifierAmount(item);

            return {
              defaultTopping: 1,
              newDefaultTopping: 0,
              modifierId: item.modifierId || item.pizzaModifierId,
              description: item.modifierName || item.pizzaModifierName,
              quantity: 1,
              isMultiplier: 0,
              Multiplier: 1,
              multiplier: 1,
              unitPrice: modifierAmount,
              subTotal: modifierAmount,
              totalValue: modifierAmount,
              note: buildModifierNote({
                ...item,
                description: item.modifierName || item.pizzaModifierName,
                subTotal: modifierAmount,
                totalValue: modifierAmount,
                pizzaRegion: 1,
              }),
              isFree: modifierAmount > 0 ? 0 : 1,
              pizzaRegion: 1,
              PizzaRegion: 1,
            };
          });

        finalProduct.itemTotal += defaultSelectedToppingPrice;
      }
      if ((config?.variant || []).length > 0) {
        const selectedVariants = getSelectedVariantsForSubmit(config.variant);
        finalProduct.variationsPrice += selectedVariants.total;
        finalProduct.selectedItemsLevel = selectedVariants.selectedItemsLevel;
        finalProduct.totalUnitPrice += finalProduct.variationsPrice;
      }
      if (
        selectedProduct &&
        !!selectedProduct.isPizza &&
        !!selectedProduct.isDeal &&
        !selectedProduct.isCreateYourOwn
      ) {
        if (!pizza) {
          toast.error("Pizza item is required");
          return;
        }
      }
      if (selectedProduct.isCreateYourOwn || selectedProduct.isPizza) {
        const newPrice = selectedProduct.isCreateYourOwn
          ? +(getSizePrice(size, activeConfig.modifications) || "0")
          : getPizzaSizeUnitPrice(size, activeConfig.qty);
        if (newPrice) {
          finalProduct.pizzaSize.price = newPrice;
          finalProduct.pizzaSize.qtyPrice = selectedProduct.isCreateYourOwn
            ? newPrice * activeConfig.qty
            : getPizzaSizeQtyPrice(size, activeConfig.qty);
          finalProduct.price = newPrice;
        }
      }
      finalProduct.itemTotal +=
        finalProduct.price + finalProduct.variationsPrice;
      finalProduct.subTotal = finalProduct.itemTotal * config.qty;
      finalProduct.totalUnitPrice = finalProduct.itemTotal;
      if (selectedProduct.taxId1 === 1 && tax1?.rate) {
        const total = +((finalProduct.subTotal * tax1?.rate) / 100).toFixed(5);
        finalProduct.totalTax1 = total;
        finalProduct.taxRate1 = tax1?.rate ?? 0;
        finalProduct.itemTotal += total;
      } else {
        finalProduct.totalTax1 = 0;
        finalProduct.taxRate1 = 0;
      }
      if (selectedProduct.taxId2 === 2 && tax2?.rate) {
        const total = +((finalProduct.subTotal * tax2?.rate) / 100).toFixed(5);
        finalProduct.totalTax2 = total;
        finalProduct.taxRate2 = tax2?.rate ?? 0;
        finalProduct.itemTotal += total;
      } else {
        finalProduct.totalTax2 = 0;
        finalProduct.taxRate2 = 0;
      }
      if (selectedProduct.taxId3 === 3 && tax3?.rate) {
        const total = +((finalProduct.subTotal * tax3.rate) / 100).toFixed(5);
        finalProduct.totalTax3 = total;
        finalProduct.taxRate3 = tax3?.rate ?? 0;
        finalProduct.itemTotal += total;
      } else {
        finalProduct.totalTax3 = 0;
        finalProduct.taxRate3 = 0;
      }
    }

    dispatch(addUpdateToCart(finalProduct, handleAddToCartSuccess));
  };

  const onClose = () => {
    setConfig(initialConfig);
    setDealVariantConfig([]);
    setCurrentPizzaIndex(0);
    setPizzasInDeal([]);
    setExpandedDealPizzaNotes({});
    modifierLoadKeyRef.current = "";
    dispatch(setSelected(undefined));
    setCheckCartConfigData([]);
    // === ADDED BY SAMPATH – 2026-01-05 ===
    // Close image preview when closing product popup
    setImagePreviewOpen(false);
    // === END SAMPATH ===
  };

  const handleAddToCartSuccess = () => {
    onClose();

    if (!showCart) {
      if (cartOpenTimeoutRef.current) {
        window.clearTimeout(cartOpenTimeoutRef.current);
      }

      cartOpenTimeoutRef.current = window.setTimeout(() => {
        dispatch(toggleCart());
        cartOpenTimeoutRef.current = null;
      }, 180);
    }
  };

  const getFirstCreateYourOwnTierCount = useCallback((item: any) => {
    const tierCounts = Object.entries(item || {})
      .map(([key, value]: any) => {
        const match = /^toppingPrice(\d+)$/i.exec(key);
        return match && Number(value || 0) > 0 ? Number(match[1]) : null;
      })
      .filter((count): count is number => count !== null)
      .sort((a, b) => a - b);

    return tierCounts[0] ?? 0;
  }, []);

  const getCreateYourOwnTierPrice = useCallback((item: any, modifications: any) => {
    if (!item) return 0;
    const chargeableCount = getChargeableCreateYourOwnToppingCount(modifications);
    const baseTierCount = getCreateYourOwnBaseTierCount();
    const tierCount =
      baseTierCount > 0
        ? baseTierCount
        : chargeableCount > 0
        ? chargeableCount
        : getFirstCreateYourOwnTierCount(item);
    if (tierCount < 0) return 0;

    const directPrice = Number(item?.[`toppingPrice${tierCount}`] || 0);
    if (directPrice > 0) return directPrice;

    const tierPrices = Object.entries(item || {})
      .map(([key, value]: any) => {
        const match = /^toppingPrice(\d+)$/i.exec(key);
        return match && Number(value || 0) > 0
          ? { count: Number(match[1]), price: Number(value) }
          : null;
      })
      .filter(Boolean) as { count: number; price: number }[];
    if (!tierPrices.length) return 0;

    const sortedTiers = tierPrices.sort((a, b) => a.count - b.count);
    const closestTier =
      sortedTiers.find((tier) => tier.count >= tierCount) ||
      sortedTiers[sortedTiers.length - 1];

    return closestTier.price;
  }, [
    getChargeableCreateYourOwnToppingCount,
    getCreateYourOwnBaseTierCount,
    getFirstCreateYourOwnTierCount,
  ]);

  const getCreateYourOwnTierPriceForCount = useCallback((item: any, tierCount: number) => {
    if (!item || tierCount < 0) return 0;

    const directPrice = Number(item?.[`toppingPrice${tierCount}`] || 0);
    if (directPrice > 0) return directPrice;

    const tierPrices = Object.entries(item || {})
      .map(([key, value]: any) => {
        const match = /^toppingPrice(\d+)$/i.exec(key);
        return match && Number(value || 0) > 0
          ? { count: Number(match[1]), price: Number(value) }
          : null;
      })
      .filter(Boolean) as { count: number; price: number }[];
    if (!tierPrices.length) return 0;

    const sortedTiers = tierPrices.sort((a, b) => a.count - b.count);
    const closestTier =
      sortedTiers.find((tier) => tier.count >= tierCount) ||
      sortedTiers[sortedTiers.length - 1];

    return closestTier.price;
  }, []);

  const getCreateYourOwnDisplayPrice = useCallback(
    (item: any, modifications: any = config?.modifications) => {
      if (!item) return 0;

      const price = Number(getCreateYourOwnTierPrice(item, modifications) || 0);
      if (price > 0) return price;

      const firstTierCount = getFirstCreateYourOwnTierCount(item);
      return Number(getCreateYourOwnTierPriceForCount(item, firstTierCount) || 0);
    },
    [
      config?.modifications,
      getCreateYourOwnTierPrice,
      getCreateYourOwnTierPriceForCount,
      getFirstCreateYourOwnTierCount,
    ],
  );

  const getCreateYourOwnDealSizePrice = useCallback(
    (item: any, pizzaConfigs: any[] = []) => {
      if (!item || !selectedProduct?.isCreateYourOwn || getDealPizzaCount(selectedProduct) <= 1) {
        return 0;
      }

      const tierCount = Math.max(
        0,
        ...pizzaConfigs.map((pizzaConfig: any) =>
          getSelectedModifierCount(pizzaConfig?.modifications),
        ),
      );

      return getCreateYourOwnTierPriceForCount(item, tierCount);
    },
    [
      getCreateYourOwnTierPriceForCount,
      getSelectedModifierCount,
      selectedProduct,
    ],
  );

  const getSizePrice = useCallback(
    (item: any, modifications: any = config?.modifications) => {
      if (selectedProduct?.isCreateYourOwn) {
        return getCreateYourOwnDisplayPrice(item, modifications);
      }

      if (selectedProduct?.isPizza) {
        const dealPizzaCount = getDealPizzaCount(selectedProduct);
        if (dealPizzaCount > 1) {
          const dealTierPrice = Number(item?.[`sizePriceX${dealPizzaCount}`] || 0);
          if (dealTierPrice > 0) return dealTierPrice;
          return Number(item?.sizePriceX1 || 0) * dealPizzaCount;
        }

        return item?.sizePriceX1;
      }
    },
    [config?.modifications, getCreateYourOwnDisplayPrice, selectedProduct]
  );

  const getPizzaSizeQtyPrice = useCallback((item: any, quantity: any) => {
    if (!item) return 0;

    const qty = Math.max(1, Math.floor(Number(quantity || 1)));
    const tierPrice = Number(item?.[`sizePriceX${qty}`] || 0);
    if (tierPrice > 0) return tierPrice;

    return Number(item?.sizePriceX1 || 0) * qty;
  }, []);

  const getPizzaSizeUnitPrice = useCallback(
    (item: any, quantity: any) => {
      const qty = Math.max(1, Math.floor(Number(quantity || 1)));
      return getPizzaSizeQtyPrice(item, qty) / qty;
    },
    [getPizzaSizeQtyPrice],
  );

  const getPizzaSizeOfferPrices = useCallback(
    (item: any) => {
      if (!item) return [];

      return Array.from({ length: 5 }, (_, index) => {
        const qty = index + 1;
        const rawPrice = Number(item?.[`sizePriceX${qty}`] || 0);

        return rawPrice > 0
          ? {
              qty,
              price: rawPrice,
            }
          : null;
      }).filter(Boolean) as { qty: number; price: number }[];
    },
    [],
  );

  const getDealPizzaBasePrice = useCallback(
    (
      pizzaConfig: any,
      pizzaIndex = 0,
      pricedModifiersOverride?: any[],
      sizePriceOverride = 0,
    ) => {
      if (!pizzaConfig) return 0;
      const sizeObj = sizes.find((i) => Number(i.sizeId) === Number(pizzaConfig?.size));
      const resolvedSizePrice =
        sizePriceOverride > 0
          ? sizePriceOverride
          : 
        sizeObj &&
        getSizePrice(sizeObj, pizzaConfig?.modifications) !== undefined &&
        getSizePrice(sizeObj, pizzaConfig?.modifications) !== ""
          ? Number(getSizePrice(sizeObj, pizzaConfig?.modifications) || 0)
          : Number(pizzaConfig?.existingSizePrice || 0);
      const sizePrice =
        getDealPizzaCount(selectedProduct) > 1
          ? pizzaIndex === 0
            ? resolvedSizePrice
            : 0
          : resolvedSizePrice;
      const crustObj = crusts.find(
        (i) => Number(i.modifierId) === Number(pizzaConfig?.crust)
      );
      const crustPrice = crustObj
        ? Number(crustObj?.modifierCost || 0)
        : Number(pizzaConfig?.existingPizzaCrust?.crustPrice || 0);
      const savedCartModifiers = Array.isArray(pizzaConfig?.savedCartModifiers)
        ? pizzaConfig.savedCartModifiers
        : [];
      const shouldUseLiveModifications = !!pizzaConfig?.modifications;
      const pricedModifiers = shouldUseLiveModifications
        ? pricedModifiersOverride || getPricedSelectedModifiers(pizzaConfig.modifications)
        : mapSavedCartModifiersForSubmit(savedCartModifiers);
      const modifierSubtotal = pricedModifiers.reduce(
        (sum: number, item: any) => sum + getModifierLineAmount(item),
        0
      );

      return sizePrice + crustPrice + modifierSubtotal;
    },
    [crusts, getPricedSelectedModifiers, getSizePrice, selectedProduct, sizes]
  );

  // === ADDED BY SAMPATH – 2026-01-05 ===
  // UI price: update footer price when Qty changes (and when variants/crust/size changes)
  const dealPricingSummary = useMemo(() => {
    if (!selectedProduct || (!isDealProduct(selectedProduct) && !hasDealPizzaSlots(selectedProduct))) {
      return { total: 0 };
    }

    const updatedPizzasInDeal = [...pizzasInDealRef.current];
    updatedPizzasInDeal[currentPizzaIndex] = configRef.current;
    const firstDealPizzaSize = sizes.find(
      (item) => Number(item?.sizeId) === Number(updatedPizzasInDeal[0]?.size),
    );
    const pricedDealModifiersBySlot =
      getCombinedDealPricedModifiersBySlot(updatedPizzasInDeal, firstDealPizzaSize);
    const combinedDealSizePrice = getCreateYourOwnDealSizePrice(
      firstDealPizzaSize,
      updatedPizzasInDeal,
    );

    const prices = updatedPizzasInDeal.map((pizzaConfig: any, pizzaIndex: number) =>
      getDealPizzaBasePrice(
        pizzaConfig,
        pizzaIndex,
        pricedDealModifiersBySlot[pizzaIndex],
        pizzaIndex === 0 ? combinedDealSizePrice : 0,
      ),
    );
    const dealVariantTotal = getSelectedVariantsForSubmit(dealVariantConfig).total;
    const total = prices.reduce((sum: number, value: number) => sum + value, 0) + dealVariantTotal;

    return {
      total: total * Math.max(1, Number(configRef.current.qty || 1)),
    };
  }, [currentPizzaIndex, dealVariantConfig, getCombinedDealPricedModifiersBySlot, getCreateYourOwnDealSizePrice, getDealPizzaBasePrice, getSelectedVariantsForSubmit, selectedProduct, config, pizzasInDeal, sizes]);

  const dealPricingDebugSummary = useMemo(() => {
    if (!selectedProduct || !hasDealPizzaSlots(selectedProduct)) {
      return null;
    }

    const updatedPizzasInDeal = [...pizzasInDealRef.current];
    updatedPizzasInDeal[currentPizzaIndex] = configRef.current;
    const firstDealPizzaSize = sizes.find(
      (item) => Number(item?.sizeId) === Number(updatedPizzasInDeal[0]?.size),
    );
    const pricedDealModifiersBySlot =
      getCombinedDealPricedModifiersBySlot(updatedPizzasInDeal, firstDealPizzaSize);
    const selectedCount = updatedPizzasInDeal.reduce(
      (sum: number, pizzaConfig: any) =>
        sum + getSelectedModifierCount(pizzaConfig?.modifications),
      0,
    );
    const combinedFreeLimit = getTotalFreeToppingCount();
    const chargedCount = Math.max(0, selectedCount - combinedFreeLimit);
    const modifierTotal = pricedDealModifiersBySlot.reduce(
      (slotSum: number, items: any[] = []) =>
        slotSum +
        items.reduce((itemSum: number, item: any) => itemSum + getModifierLineAmount(item), 0),
      0,
    );
    const baseSizeTotal = getCreateYourOwnDealSizePrice(
      firstDealPizzaSize,
      updatedPizzasInDeal,
    );
    const fallbackBaseTotal = baseSizeTotal || Number(firstDealPizzaSize?.sizePriceX1 || 0);
    const reason =
      chargedCount > 0 && modifierTotal <= 0
        ? "Extra count found, but topping price/subtotal is 0"
        : chargedCount > 0
        ? "Extras included in footer total"
        : "No extra charge until selected count passes free limit";

    return {
      selectedCount,
      combinedFreeLimit,
      chargedCount,
      modifierTotal,
      baseSizeTotal: fallbackBaseTotal,
      footerTotal: dealPricingSummary.total,
      reason,
    };
  }, [
    config,
    currentPizzaIndex,
    dealPricingSummary.total,
    getCombinedDealPricedModifiersBySlot,
    getCreateYourOwnDealSizePrice,
    getSelectedModifierCount,
    getTotalFreeToppingCount,
    pizzasInDeal,
    selectedProduct,
    sizes,
  ]);

  const uiTotalPrice = useMemo(() => {
    if (!selectedProduct) return 0;

    if (isDealProduct(selectedProduct) || hasDealPizzaSlots(selectedProduct)) {
      return dealPricingSummary.total;
    }

    let basePrice =
      settings.ApplyTakeOutDeliveryPrice !== 0
        ? deliveryType !== "pickup"
          ? selectedProduct?.deliveryprice || selectedProduct.price
          : selectedProduct?.takeoutprice || selectedProduct.price
        : !!selectedProduct.isDeal || !!selectedProduct.isCreateYourOwn
        ? 0
        : selectedProduct.price;

    if (selectedProduct.isPizza || selectedProduct.isCreateYourOwn) {
      const sizeObj = sizes.find((i) => i.sizeId === config.size);
      const sizePrice = selectedProduct.isCreateYourOwn
        ? Number(getSizePrice(sizeObj, config.modifications) || 0)
        : getPizzaSizeUnitPrice(sizeObj, config.qty);
      if (!Number.isNaN(sizePrice) && sizePrice > 0) {
        basePrice = sizePrice;
      }
    }

    let unit = Number(basePrice || 0);

    const crustObj = crusts.find((i) => i.modifierId === config.crust);
    if (crustObj?.modifierCost) unit += Number(crustObj.modifierCost || 0);

    if (Array.isArray(config.variant)) {
      unit += getSelectedVariantsForSubmit(config.variant).total;
    }

    if (selectedProduct.isPizza || selectedProduct.isCreateYourOwn) {
      const modifierPreviewTotal = config?.modifications
        ? getPricedSelectedModifiers(config.modifications).reduce(
            (sum: number, item: any) => sum + getModifierLineAmount(item),
            0,
          )
        : (selectedProduct.defaultPizzaToppings || []).reduce(
            (sum: number, item: any) =>
              item?.selected === false ? sum : sum + getModifierAmount(item),
            0,
          );

      unit += modifierPreviewTotal;
    }

    const qty = Math.max(1, Number(config.qty || 1));
    return unit * qty;
  }, [
    dealPricingSummary.total,
    selectedProduct,
    settings,
    deliveryType,
    sizes,
    crusts,
    config.size,
    config.crust,
    config.qty,
    config.variant,
    config.modifications,
    getSelectedVariantsForSubmit,
    getSizePrice,
    getPizzaSizeUnitPrice,
    getPricedSelectedModifiers,
    selectedProduct?.defaultPizzaToppings,
  ]);

  const isSharedDealSizeLocked =
    getDealPizzaCount(selectedProduct) > 1 && currentPizzaIndex > 0;
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // One-page (NO STEPS): keep ALL variant selections on the same page.
  // We store each level selection in config.variant[levelIndex].
  const useDealLevelVariants =
    hasDealPizzaSlots(selectedProduct) ||
    (!!selectedProduct?.isDeal && !!selectedProduct?.isPizza);

  const setVariantAt = (levelIndex: number, value: any) => {
    if (useDealLevelVariants) {
      setDealVariantConfig((prev: any[]) => {
        const next = safeClone(Array.isArray(prev) ? prev : []);
        next[levelIndex] = value || {};
        return next;
      });
      return;
    }

    updateCurrentPizzaConfig((prev: any) => {
      const next = safeClone(prev);
      if (!Array.isArray(next.variant)) next.variant = [];
      next.variant[levelIndex] = value || {};
      return next;
    });
  };

  const getVariantAt = useCallback((levelIndex: number) => {
    const v = useDealLevelVariants
      ? dealVariantConfig?.[levelIndex]
      : (config as any)?.variant?.[levelIndex];
    return v && typeof v === "object" ? v : {};
  }, [config, dealVariantConfig, useDealLevelVariants]);

  const isVariantLevelValid = useCallback((levelIndex: number) => {
    const level = variants?.[levelIndex];
    if (!level) return true;

    const selectedObj = getVariantAt(levelIndex) || {};
    const count = Object.keys(selectedObj).length;

    const min = Number(level?.mandatoryLevel || 0);
    const max = Number(level?.qtyLevel || 0);

    // If max not provided (0), treat as no upper limit (but keep min)
    const maxOk = max > 0 ? count <= max : true;
    return count >= min && maxOk;
  }, [getVariantAt, variants]);

  const areAllVariantLevelsValid = useMemo(() => {
    if (!selectedProduct) return true;
    if (!Array.isArray(variants) || variants.length === 0) return true;
    return variants.every((_: any, idx: number) => isVariantLevelValid(idx));
  }, [variants, selectedProduct, isVariantLevelValid]);

  const areAllDealPizzaItemsSelected = useMemo(() => {
    if (!hasDealPizzaSlots(selectedProduct)) return true;
    if (selectedProduct?.isCreateYourOwn) return true;

    const updatedPizzasInDeal = [...pizzasInDeal];
    updatedPizzasInDeal[currentPizzaIndex] = config;
    const dealPizzaCount = getDealPizzaCount(selectedProduct);

    return (
      updatedPizzasInDeal.length >= dealPizzaCount &&
      updatedPizzasInDeal.every((pizzaConfig: any) => Number(pizzaConfig?.id || 0) > 0)
    );
  }, [config, currentPizzaIndex, pizzasInDeal, selectedProduct]);

  const hasResolvedPizzaCrust = useCallback(
    (pizzaConfig: any) => {
      if (!pizzaConfig) return false;

      const selectedCrust = crusts.find(
        (item: any) => Number(item?.modifierId) === Number(pizzaConfig?.crust),
      );

      return (
        !!selectedCrust ||
        !!String(pizzaConfig?.existingPizzaCrust?.crustName || "").trim() ||
        !!String(pizzaConfig?.existingPizzaCrust?.modifierName || "").trim()
      );
    },
    [crusts],
  );

  const getPizzaSlotMissingSelections = useCallback(
    (pizzaConfig: any, requirePizzaItem: boolean) => {
      const missing: string[] = [];
      const hasPizzaItem = Number(pizzaConfig?.id || 0) > 0;

      if (requirePizzaItem && !hasPizzaItem) {
        missing.push("pizza item");
        return missing;
      }

      if (!Number(pizzaConfig?.size || 0)) missing.push("size");
      if (spiceData.length > 0 && !String(pizzaConfig?.spiceLevel || "").trim()) {
        missing.push("spice");
      }
      if (!hideServiceSubPizzaOptions && !hasResolvedPizzaCrust(pizzaConfig)) {
        missing.push("crust");
      }
      if (
        Number(selectedProduct?.isCreateYourOwn || 0) === 1 &&
        !hasAnySelectedModifier(pizzaConfig?.modifications)
      ) {
        missing.push("toppings");
      }

      return missing;
    },
    [
      hasAnySelectedModifier,
      hasResolvedPizzaCrust,
      hideServiceSubPizzaOptions,
      selectedProduct,
      spiceData.length,
    ],
  );

  const pizzaRequirementMessages = useMemo(() => {
    if (!selectedProduct?.isPizza && !selectedProduct?.isCreateYourOwn) return [];

    const messages: string[] = [];
    const dealPizzaCount = getDealPizzaCount(selectedProduct);
    const isDealPizza = dealPizzaCount > 0;

    const checkPizzaConfig = (pizzaConfig: any, label: string, requirePizzaItem: boolean) => {
      getPizzaSlotMissingSelections(pizzaConfig, requirePizzaItem).forEach((item) => {
        messages.push(`${label}: select ${item}`);
      });
    };

    if (isDealPizza) {
      const updatedPizzasInDeal = [...pizzasInDeal];
      updatedPizzasInDeal[currentPizzaIndex] = config;

      for (let index = 0; index < dealPizzaCount; index += 1) {
        checkPizzaConfig(
          updatedPizzasInDeal[index] || createInitialPizzaConfig(),
          `${dealSlotLabel} ${index + 1}`,
          !!selectedProduct.isDeal && !selectedProduct.isCreateYourOwn,
        );
      }

      return messages;
    }

    checkPizzaConfig(config, dealSlotLabel, false);
    return messages;
  }, [config, currentPizzaIndex, dealSlotLabel, getPizzaSlotMissingSelections, pizzasInDeal, selectedProduct]);

  const hasRequiredCreateYourOwnToppings = useMemo(() => {
    if (!selectedProduct?.isCreateYourOwn) return true;

    if (hasDealPizzaSlots(selectedProduct)) {
      const updatedPizzasInDeal = [...pizzasInDeal];
      updatedPizzasInDeal[currentPizzaIndex] = config;
      const dealPizzaCount = getDealPizzaCount(selectedProduct);

      for (let index = 0; index < dealPizzaCount; index += 1) {
        if (!hasAnySelectedModifier(updatedPizzasInDeal[index]?.modifications)) {
          return false;
        }
      }

      return true;
    }

    return hasAnySelectedModifier(config.modifications);
  }, [config, currentPizzaIndex, hasAnySelectedModifier, pizzasInDeal, selectedProduct]);
  const optionRequirementMessages = useMemo(() => {
    if (!selectedProduct || !Array.isArray(variants) || variants.length === 0) return [];

    return variants.reduce((messages: string[], level: any, idx: number) => {
      if (!isVariantLevelValid(idx)) {
        messages.push(
          `${level?.levelName || level?.name || level?.nameLevel || `Option ${idx + 1}`}: select minimum ${
            level?.mandatoryLevel
          }`,
        );
      }

      return messages;
    }, []);
  }, [variants, selectedProduct, isVariantLevelValid]);
  const footerValidationMessage = [
    ...pizzaRequirementMessages,
    ...optionRequirementMessages,
  ]
    .filter(Boolean)
    .join("  |  ");
  const areAllRequiredSelectionsComplete =
    areAllVariantLevelsValid &&
    areAllDealPizzaItemsSelected &&
    pizzaRequirementMessages.length === 0 &&
    hasRequiredCreateYourOwnToppings;
  // === END SAMPATH ===


  const pizzaDropdownOptions = useMemo(
    () =>
      hasDealPizzaSlots(selectedProduct)
        ? mergePizzaProducts(dealPizzaOptions, pizzaList)
        : pizzaList,
    [dealPizzaOptions, pizzaList, selectedProduct],
  );

  const selectedPizzaOption = useMemo(
    () => pizzaDropdownOptions.find((item) => item.productId === config.id),
    [pizzaDropdownOptions, config.id]
  );

  const isHalfHalfNotAllowedForPizzaConfig = useCallback(
    (pizzaConfig: any) => {
      const pizzaProductId = Number(pizzaConfig?.id || selectedProduct?.productId || 0);
      const pizzaProduct =
        pizzaDropdownOptions.find(
          (item: any) => Number(item?.productId || 0) === pizzaProductId,
        ) || selectedProduct;

      return isHalfHalfNotAllowedProduct(pizzaProduct);
    },
    [pizzaDropdownOptions, selectedProduct],
  );

  const selectedSize = useMemo(
    () => sizes.find((item) => item.sizeId === config.size),
    [sizes, config.size]
  );

  const selectedSizeOfferPrices = useMemo(
    () =>
      selectedProduct?.isPizza &&
      !selectedProduct?.isCreateYourOwn &&
      !hasDealPizzaSlots(selectedProduct)
        ? getPizzaSizeOfferPrices(selectedSize)
        : [],
    [getPizzaSizeOfferPrices, selectedProduct, selectedSize],
  );

  useEffect(() => {
    const updateOfferScrollState = () => {
      const node = offerScrollRef.current;
      if (!node) {
        setOfferScrollState({
          canScrollLeft: false,
          canScrollRight: false,
        });
        return;
      }

      setOfferScrollState({
        canScrollLeft: node.scrollLeft > 2,
        canScrollRight: node.scrollLeft + node.clientWidth < node.scrollWidth - 2,
      });
    };

    const node = offerScrollRef.current;
    updateOfferScrollState();
    const frame = window.requestAnimationFrame(updateOfferScrollState);
    window.addEventListener("resize", updateOfferScrollState);
    node?.addEventListener("scroll", updateOfferScrollState);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateOfferScrollState);
      node?.removeEventListener("scroll", updateOfferScrollState);
    };
  }, [selectedSizeOfferPrices, config.size]);

  const selectedCrust = useMemo(
    () =>
      crusts.find(
        (item) => Number(item.modifierId) === Number(config.crust),
      ),
    [crusts, config.crust]
  );

  const selectedSpice = useMemo(
    () => spiceData.find((item) => item.modifierName === config.spiceLevel),
    [spiceData, config.spiceLevel]
  );

  const openPizzaCustomize = () => {
    if (!selectedProduct || !config.size) {
      toast.error("Please select size first");
      return;
    }
    const sizeObj = sizes.find((item) => Number(item?.sizeId) === Number(config.size));

    setshowModification({
      productId: config.id || selectedProduct.productId,
      pizzaSizeId: config.size,
      sizeId: config.size,
      modifications: config.modifications,
      combinedFreeLimit: getTotalFreeToppingCount(),
      otherSelectedToppingCount: 0,
      hideHalfSelection: isHalfHalfNotAllowedForPizzaConfig(config),
      toppingPriceTiers: getToppingPriceTiers(sizeObj),
    });
  };

  const activateDealPizzaSlot = useCallback(
    (slotIndex: number) => {
      if (!hasDealPizzaSlots(selectedProduct)) return;
      const nextConfig = clonePizzaConfig(
        pizzasInDealRef.current?.[slotIndex] || createInitialPizzaConfig(),
      );
      setCurrentPizzaIndex(slotIndex);
      configRef.current = nextConfig;
      setConfig(nextConfig);
    },
    [clonePizzaConfig, selectedProduct],
  );

  const updateDealPizzaSlotConfig = useCallback(
    (slotIndex: number, updater: any) => {
      const currentSlot = clonePizzaConfig(
        pizzasInDealRef.current?.[slotIndex] || createInitialPizzaConfig(),
      );
      const next =
        typeof updater === "function"
          ? updater(currentSlot)
          : {
              ...currentSlot,
              ...updater,
            };

      updateDealPizzaSlot(slotIndex, next);
      if (slotIndex === currentPizzaIndex) {
        configRef.current = next;
        setConfig(next);
      }
    },
    [clonePizzaConfig, currentPizzaIndex, updateDealPizzaSlot],
  );

  const openDealPizzaCustomize = (slotIndex: number) => {
    const slotConfig = pizzasInDealRef.current?.[slotIndex] || createInitialPizzaConfig();
    if (!selectedProduct || !slotConfig.size) {
      toast.error("Please select size first");
      return;
    }
    const sizeObj = sizes.find((item) => Number(item?.sizeId) === Number(slotConfig.size));

    activateDealPizzaSlot(slotIndex);
    const otherSelectedToppingCount = pizzasInDealRef.current.reduce(
      (sum: number, pizzaConfig: any, pizzaIndex: number) =>
        pizzaIndex === slotIndex
          ? sum
          : sum + getSelectedModifierCount(pizzaConfig?.modifications),
      0,
    );

    setshowModification({
      productId: slotConfig.id || selectedProduct.productId,
      pizzaSizeId: slotConfig.size,
      sizeId: slotConfig.size,
      modifications: slotConfig.modifications,
      combinedFreeLimit: getTotalFreeToppingCount(),
      otherSelectedToppingCount,
      hideHalfSelection: isHalfHalfNotAllowedForPizzaConfig(slotConfig),
      toppingPriceTiers: getToppingPriceTiers(sizeObj),
    });
  };

  const goToDealPizza = (nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= pizzasInDeal.length) return;

    const updatedPizzas = [...pizzasInDealRef.current];
    updatedPizzas[currentPizzaIndex] = clonePizzaConfig(configRef.current);
    if (getDealPizzaCount(selectedProduct) > 1 && nextIndex > 0) {
      const sharedFirstPizza = updatedPizzas[0] || {};
      const nextPizza = updatedPizzas[nextIndex] || createInitialPizzaConfig();

      if (sharedFirstPizza?.size && !nextPizza?.size) {
        updatedPizzas[nextIndex] = clonePizzaConfig({
          ...nextPizza,
          size: sharedFirstPizza.size,
          existingPizzaSize: sharedFirstPizza.existingPizzaSize,
          existingSizePrice: sharedFirstPizza.existingSizePrice,
          qty: sharedFirstPizza.qty || configRef.current?.qty || 1,
        });
      }
    }
    pizzasInDealRef.current = updatedPizzas;
    setPizzasInDeal(updatedPizzas);
    setCurrentPizzaIndex(nextIndex);
    const nextConfig = clonePizzaConfig(updatedPizzas[nextIndex]);
    configRef.current = nextConfig;
    setConfig(nextConfig);
  };

  const applySharedDealSize = useCallback(
    (nextSizeId: number, chosenSize: any) => {
      const normalizedSize = chosenSize
        ? {
            pizzasizeId: chosenSize.sizeId,
            size: chosenSize.sizeName,
            sizePriceX1: Number(chosenSize?.sizePriceX1 || 0),
            sizePriceX2: Number(chosenSize?.sizePriceX2 || 0),
            sizePriceX3: Number(chosenSize?.sizePriceX3 || 0),
            sizePriceX4: Number(chosenSize?.sizePriceX4 || 0),
            sizePriceX5: Number(chosenSize?.sizePriceX5 || 0),
          }
        : null;
      const normalizedSizePrice = Number(getSizePrice(chosenSize) || 0);

      setPizzasInDeal((prev) => {
        const next = prev.map((pizza: any) =>
          clonePizzaConfig({
            ...pizza,
            size: nextSizeId,
            existingPizzaSize: normalizedSize,
            existingSizePrice: normalizedSizePrice,
            savedCartModifiers: [],
            modifications: undefined,
          }),
        );
        pizzasInDealRef.current = next;
        return next;
      });

      setConfig((prev: any) => {
        const next = {
          ...prev,
          size: nextSizeId,
          existingPizzaSize: normalizedSize,
          existingSizePrice: normalizedSizePrice,
          savedCartModifiers: [],
          modifications: undefined,
        };
        configRef.current = next;
        return next;
      });
    },
    [clonePizzaConfig, getSizePrice],
  );

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // Theme-based colors for footer controls (transparent mix from theme primary).
  const themePrimaryBg = "rgb(var(--color-primary) / 0.12)";
  const themePrimaryBgHover = "rgb(var(--color-primary) / 0.18)";
  const themePrimarySolid = "rgb(var(--color-primary) / 1)";
  // === END SAMPATH ===

  return (
    <Dialog
      onClose={onClose}
      open={!!selectedProduct}
      maxWidth="sm"
      fullWidth
      className="select-none"
      // === ADDED BY SAMPATH – 2026-03-16 ===
      // Full screen on mobile + better popup spacing like modern food apps
      fullScreen={isMobilePopup}
      PaperProps={{
        className: isMobilePopup
          ? "!rounded-[14px] overflow-hidden bg-white shadow-2xl"
          : "!rounded-[22px] overflow-hidden bg-white shadow-2xl",
        sx: {
          borderRadius: isMobilePopup ? "14px" : "22px",
          margin: isMobilePopup ? "6px" : 1.5,
          width: isMobilePopup ? "calc(100% - 12px)" : undefined,
          maxWidth: isMobilePopup ? "100%" : 640,
          height: isMobilePopup ? "calc(100dvh - 12px)" : "auto",
          maxHeight: isMobilePopup ? "calc(100dvh - 12px)" : "92vh",
        },
      }}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(15, 23, 42, 0.42)",
          backdropFilter: "blur(8px)",
        },
      }}
      // === END SAMPATH ===
    >
      <style>{PRODUCT_POPUP_PLACE_ORDER_BUTTON_CSS}</style>
      {selectedProduct && (
        // === ADDED BY SAMPATH – 2026-03-16 ===
        // Better app-style layout with fixed footer and scrollable body
        <div
          className="flex flex-col bg-white"
          style={{ height: isMobilePopup ? "calc(100dvh - 12px)" : "92vh" }}
        >
          {/* === END SAMPATH === */}
          <div
            className="deal-popup-scroll flex-1 overflow-y-auto bg-[#fcfcfa]"
            onScroll={handleProductPopupScroll}
          >
            <div className="sticky top-0 z-30 h-0">
              <div
                className={`flex h-[58px] items-center gap-4 border-b border-black/15 bg-white px-4 transition-opacity duration-200 ${
                  showScrolledProductHeader
                    ? "opacity-100"
                    : "pointer-events-none opacity-0"
                }`}
              >
                <button
                  onClick={onClose}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[24px] leading-none text-black hover:bg-black/5 transition"
                  aria-label="Close"
                  type="button"
                >
                  ×
                </button>
                <div className="min-w-0 truncate text-[18px] font-extrabold uppercase leading-none tracking-[0.01em] text-black">
                  {selectedProduct.productName}
                </div>
              </div>
            </div>

            <div className="relative bg-white">
              <img
                src={selectedProduct?.imageUrl || noImage}
                className="w-full h-[240px] sm:h-[300px] object-cover cursor-zoom-in"
                alt="product"
                onClick={() => setImagePreviewOpen(true)}
              />

              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent" />

              <button
                onClick={onClose}
                className={`absolute top-3 left-3 h-11 w-11 rounded-full bg-white/95 shadow-md border border-black/5 flex items-center justify-center hover:bg-white transition ${
                  showScrolledProductHeader ? "opacity-0 pointer-events-none" : "opacity-100"
                }`}
                aria-label="Close"
                type="button"
              >
                ✕
              </button>
            </div>

            {/* === ADDED BY SAMPATH – 2026-03-16 === */}
            {/* More premium content area spacing */}
            <div className="px-4 sm:px-5 pt-4 pb-5 flex flex-col gap-5">
            {/* === END SAMPATH === */}
            <div className="flex items-start justify-between gap-4">
              {/* === ADDED BY SAMPATH – 2026-03-16 === */}
              {/* Stronger heading style like second reference */}
              <div className="min-w-0 flex-1">
                <h4 className="font-extrabold text-[18px] sm:text-[20px] leading-[1.2] uppercase tracking-[0.01em] text-[#222] inline-block">
                  {selectedProduct.productName}
                </h4>
              {/* === END SAMPATH === */}

                {Number(selectedProduct.price || 0) > 0 && (
                  <p className="text-[15px] font-semibold text-[#24513e] mt-2">
                    {formatMoney(selectedProduct.price)}
                  </p>
                )}

                {!!selectedProduct.productDescription && (
                  <p className="text-[13px] sm:text-[14px] text-gray-600 mt-2 leading-6 capitalize">
                    {selectedProduct.productDescription.toLowerCase()}
                  </p>
                )}

              </div>

              {showSpecialDiscountBadge && (
                <div className="mt-1 inline-flex shrink-0 items-center gap-1 rounded-[2px] bg-[#f10505] px-2 py-1 text-white shadow-[0_5px_14px_rgba(220,38,38,0.22)]">
                  <span className="text-[11px] font-bold leading-none tracking-normal">
                    SPL
                  </span>
                  <span className="text-[12px] font-bold leading-none tracking-normal">
                    {specialDiscountPercent}% off
                  </span>
                  <span className="inline-flex h-[11px] w-[11px] items-center justify-center rounded-full border border-white text-[8px] font-bold leading-none">
                    i
                  </span>
                </div>
              )}
            </div>

            <div className="border-t border-black/10 pt-4">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <p className="text-[17px] font-bold tracking-[0.01em] text-[#222]">
                    Customize Your Item
                  </p>
                  <p className="text-[12px] text-gray-500 mt-1">
                    Choose your options and add a note if needed.
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  {combinedDealToppingSummary.combinedLimit > 0 && (
                    <div className="rounded-full bg-[#fff4e5] px-3 py-1 text-[11px] font-semibold text-[#8a4b00]">
                      Combined {formatToppingCount(combinedDealToppingSummary.combinedLimit)} • Selected{" "}
                      {formatToppingCount(combinedDealToppingSummary.selectedCount)} • Remaining{" "}
                      {formatToppingCount(combinedDealToppingSummary.remainingCount)}
                    </div>
                  )}
                  {!!variants.length && (
                    <div className="rounded-full bg-[#eef4ef] px-3 py-1 text-[11px] font-semibold text-[#24513e]">
                      {variants.length} section{variants.length > 1 ? "s" : ""}
                    </div>
                  )}
                </div>
              </div>
              {dealPricingDebugSummary && combinedDealToppingSummary.combinedLimit > 0 && (
                
                <div className="hidden mt-3 rounded-xl border border-[#f1ddbd] bg-[#fffaf1] px-3 py-2 text-[11px] leading-5 text-[#6d4308]">
            {/*    
                <div className="mt-3 rounded-xl border border-[#f1ddbd] bg-[#fffaf1] px-3 py-2 text-[11px] leading-5 text-[#6d4308]">

*/}
{/* Note: Calculation debug summary - uncomment this div when need to show calculation    Sampath calculation in popup */}

                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 font-semibold">
                    <span>Base {formatMoney(dealPricingDebugSummary.baseSizeTotal)}</span>
                    <span>
                      Free {dealPricingDebugSummary.combinedFreeLimit} / Selected{" "}
                      {formatToppingCount(dealPricingDebugSummary.selectedCount)}
                    </span>
                    <span>Charge count {formatToppingCount(dealPricingDebugSummary.chargedCount)}</span>
                    <span>Extra total {formatMoney(dealPricingDebugSummary.modifierTotal)}</span>
                    <span>Footer {formatMoney(dealPricingDebugSummary.footerTotal)}</span>
                  </div>
                  <div className="mt-1 text-[10px] font-medium text-[#9a5a05]">
                    {dealPricingDebugSummary.reason}
                  </div>
                </div>
              )}
            </div> 

            {!!selectedProduct.isPizza && (
              <div className="flex flex-col gap-4">
                {hasDealPizzaSlots(selectedProduct) && pizzasInDeal.length > 0 ? (
                  pizzasInDeal.map((pizzaConfig: any, pizzaIndex: number) => {
                    const slotSize = sizes.find(
                      (item) => Number(item.sizeId) === Number(pizzaConfig.size),
                    );
                    const slotSpice = spiceData.find(
                      (item: any) => item.modifierName === pizzaConfig.spiceLevel,
                    );
                    const slotCrust = crusts.find(
                      (item) => Number(item.modifierId) === Number(pizzaConfig.crust),
                    );
                    const slotPizzaOption = pizzaDropdownOptions.find(
                      (item) => Number(item.productId) === Number(pizzaConfig.id),
                    );
                    const selectedToppings = getPricedSelectedModifiers(
                      pizzaConfig.modifications,
                    );
                    const slotDefaults = buildToppingDisplayGroups(
                      selectedToppings.filter(
                        (item: any) => Number(item?.defaultTopping) === 1,
                      ),
                    );
                    const slotExtras = buildToppingDisplayGroups(
                      selectedToppings.filter(
                        (item: any) => Number(item?.defaultTopping) !== 1,
                      ),
                    );
                    const slotToppingCount = getSelectedModifierCount(
                      pizzaConfig.modifications,
                    );
                    const slotMissingSelections = getPizzaSlotMissingSelections(
                      pizzaConfig,
                      !!selectedProduct.isDeal && !selectedProduct.isCreateYourOwn,
                    );
                    const isSlotRequiredIncomplete = slotMissingSelections.length > 0;
                    const dealPizzaSlotTitle = `${selectedProduct.productName || dealSlotLabel} ${dealSlotLabel} ${
                      pizzaIndex + 1
                    }`;
                    const showSlotSizeSelector =
                      getDealPizzaCount(selectedProduct) <= 1 || pizzaIndex === 0;
                    const hasSlotToppingSummary =
                      slotDefaults.whole.length > 0 ||
                      slotDefaults.left.length > 0 ||
                      slotDefaults.right.length > 0 ||
                      slotExtras.whole.length > 0 ||
                      slotExtras.left.length > 0 ||
                      slotExtras.right.length > 0;

                    return (
                      <div
                        key={`deal-pizza-slot-${pizzaIndex}`}
                        className="rounded-[20px] border border-[#e4ddd1] bg-white px-4 py-4 shadow-[0_10px_30px_rgba(0,0,0,0.04)]"
                        onFocus={() => activateDealPizzaSlot(pizzaIndex)}
                        onClick={() => activateDealPizzaSlot(pizzaIndex)}
                      >
                        <div className="mb-3 flex items-center justify-between gap-3 border-b border-[#eee7da] pb-2">
                          <div className="flex min-w-0 items-center gap-2">
                            <div className="text-[16px] font-extrabold text-[#222]">
                              {dealPizzaSlotTitle}
                            </div>
                            {(selectedProduct.isCreateYourOwn ||
                              !(pizzaConfig.pizzaName || slotPizzaOption?.productName)) && (
                              <p className="truncate text-[12px] text-gray-500">
                                {selectedProduct.isCreateYourOwn
                                  ? `${dealSlotLabel} ${pizzaIndex + 1} of ${pizzasInDeal.length}`
                                  : `Choose the ${dealSlotLabel.toLowerCase()} for this slot.`}
                              </p>
                            )}
                            {!showSlotSizeSelector && !!slotSize && (
                              <p className="truncate text-[12px] font-semibold text-[#5d574c]">
                                {slotSize.sizeName}
                              </p>
                            )}
                            {!!(pizzaConfig.pizzaName || slotPizzaOption?.productName) && (
                              <p className="truncate text-[12px] text-gray-500">
                                {pizzaConfig.pizzaName || slotPizzaOption?.productName}
                              </p>
                            )}
                          </div>
                          <div
                            className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                              isSlotRequiredIncomplete
                                ? "required-pulse-badge bg-red-600 text-white shadow-[0_0_0_1px_rgba(220,38,38,0.18)]"
                                : "bg-[#f4f4f2] text-gray-500"
                            }`}
                          >
                            Required
                          </div>
                        </div>

                        {!selectedProduct.isCreateYourOwn && (
                          <div className="mb-3">
                            <Select
                              fullWidth
                              label={`${dealSlotLabel} Item`}
                              value={pizzaConfig.id || ""}
                              onValueChange={(value) => {
                                const nextProductId = Number(value);
                                const chosenPizza = pizzaDropdownOptions.find(
                                  (item) => item.productId === nextProductId,
                                );
                                const nextSharedSizeId = Number(
                                  pizzasInDealRef.current?.[0]?.size ||
                                    configRef.current?.size ||
                                    0,
                                );
                                const firstSpice = spiceData[0];
                                const firstSpiceName = String(
                                  firstSpice?.modifierName || "",
                                ).trim();
                                const firstSpiceLevel = firstSpiceName
                                  ? {
                                      spiceId: firstSpice?.modifierId || 1,
                                      spicename: firstSpiceName,
                                    }
                                  : null;

                                updateDealPizzaSlotConfig(pizzaIndex, (prev: any) => ({
                                  ...prev,
                                  id: nextProductId,
                                  pizzaName: chosenPizza?.productName || "",
                                  pizzaProductCode: chosenPizza?.productCode || "",
                                  size:
                                    getDealPizzaCount(selectedProduct) > 1
                                      ? nextSharedSizeId
                                      : prev.size,
                                  crust: 0,
                                  spiceLevel: firstSpiceName,
                                  existingSpiceLevel: firstSpiceLevel,
                                  variant: [],
                                  savedCartModifiers: [],
                                  modifications: undefined,
                                }));
                                if (getDealPizzaCount(selectedProduct) <= 1) {
                                  setSizes([]);
                                }
                                setCrusts([]);
                              }}
                            >
                              {dealPizzaOptionsLoading && !pizzaDropdownOptions.length && (
                                <MenuItem disabled value="">
                                  Loading pizzas...
                                </MenuItem>
                              )}
                              {pizzaDropdownOptions.map((item: any) => (
                                <MenuItem key={item.productId} value={item.productId}>
                                  {item.productName}
                                </MenuItem>
                              ))}
                            </Select>
                          </div>
                        )}

                        <div
                          className={`grid grid-cols-1 gap-3 ${
                            hideServiceSubPizzaOptions
                              ? "md:grid-cols-[minmax(155px,260px)]"
                              : showSlotSizeSelector
                              ? "md:grid-cols-[minmax(155px,1fr)_minmax(170px,1.15fr)_minmax(170px,1.15fr)]"
                              : "md:grid-cols-2"
                          }`}
                        >
                          {showSlotSizeSelector && (
                            <div>
                              <div className="text-[13px] font-bold text-[#222] mb-2">
                                Size
                              </div>
                              <Select
                                fullWidth
                                label=""
                                value={pizzaConfig.size || ""}
                                inputProps={{ "aria-label": "Size" }}
                                onValueChange={(value) => {
                                  const nextSizeId = Number(value);
                                  const chosenSize = sizes.find(
                                    (item) => Number(item?.sizeId) === nextSizeId,
                                  );

                                  if (
                                    getDealPizzaCount(selectedProduct) > 1 &&
                                    pizzaIndex === 0
                                  ) {
                                    applySharedDealSize(nextSizeId, chosenSize);
                                    return;
                                  }

                                  updateDealPizzaSlotConfig(pizzaIndex, (prev: any) => ({
                                    ...prev,
                                    size: nextSizeId,
                                    existingPizzaSize: chosenSize
                                      ? {
                                          pizzasizeId: chosenSize.sizeId,
                                          size: chosenSize.sizeName,
                                          sizePriceX1: Number(chosenSize?.sizePriceX1 || 0),
                                          sizePriceX2: Number(chosenSize?.sizePriceX2 || 0),
                                          sizePriceX3: Number(chosenSize?.sizePriceX3 || 0),
                                          sizePriceX4: Number(chosenSize?.sizePriceX4 || 0),
                                          sizePriceX5: Number(chosenSize?.sizePriceX5 || 0),
                                        }
                                      : prev.existingPizzaSize,
                                    existingSizePrice: Number(
                                      getSizePrice(chosenSize, prev.modifications) || 0,
                                    ),
                                    savedCartModifiers: [],
                                    modifications: undefined,
                                  }));
                                }}
                              >
                                {sizes.map((item) => (
                                  <MenuItem key={item.sizeId} value={item.sizeId}>
                                    {item.sizeName} [
                                    {formatMoney(
                                      selectedProduct?.isPizza &&
                                        !selectedProduct?.isCreateYourOwn &&
                                        !hasDealPizzaSlots(selectedProduct)
                                        ? getPizzaSizeQtyPrice(item, 1)
                                        : getSizePrice(item, pizzaConfig.modifications) || 0,
                                    )}
                                    ]
                                  </MenuItem>
                                ))}
                              </Select>
                            </div>
                          )}

                          {!hideServiceSubPizzaOptions && (
                          <div>
                            <div className="text-[13px] font-bold text-[#222] mb-2">
                              Special Instructions
                            </div>
                            <Select
                              fullWidth
                              label=""
                              value={pizzaConfig.spiceLevel || ""}
                              inputProps={{ "aria-label": "Select Spice" }}
                              onValueChange={(value) => {
                                const chosenSpice = spiceData.find(
                                  (item: any) => item?.modifierName === value,
                                );

                                updateDealPizzaSlotConfig(pizzaIndex, (prev: any) => ({
                                  ...prev,
                                  spiceLevel: value,
                                  existingSpiceLevel: chosenSpice
                                    ? {
                                        spiceId: chosenSpice.modifierId || 1,
                                        spicename: chosenSpice.modifierName || value,
                                      }
                                    : prev.existingSpiceLevel,
                                }));
                              }}
                            >
                              {spiceData.map((item: any) => (
                                <MenuItem
                                  key={item.modifierId || item.modifierName}
                                  value={item.modifierName}
                                >
                                  {item.modifierName}
                                </MenuItem>
                              ))}
                            </Select>
                          </div>
                          )}

                          {!hideServiceSubPizzaOptions && (
                          <div>
                            <div className="text-[13px] font-bold text-[#222] mb-2">
                              Crusts
                            </div>
                            <Select
                              fullWidth
                              label=""
                              value={
                                slotCrust ? Number(slotCrust.modifierId) : pizzaConfig.crust || ""
                              }
                              inputProps={{ "aria-label": "Crusts" }}
                              onValueChange={(value) => {
                                const nextCrustId = Number(value);
                                const chosenCrust = crusts.find(
                                  (item) => Number(item?.modifierId) === nextCrustId,
                                );

                                updateDealPizzaSlotConfig(pizzaIndex, (prev: any) => ({
                                  ...prev,
                                  crust: nextCrustId,
                                  existingPizzaCrust: chosenCrust
                                    ? {
                                        crustId: chosenCrust.modifierId,
                                        crustName: chosenCrust.modifierName,
                                        crustPrice: chosenCrust.modifierCost,
                                      }
                                    : prev.existingPizzaCrust,
                                }));
                              }}
                            >
                              {crusts.map((item) => (
                                <MenuItem
                                  key={item.modifierId}
                                  value={Number(item.modifierId)}
                                >
                                  {item.modifierName}
                                  {item.modifierCost
                                    ? ` [+${formatMoney(item.modifierCost)}]`
                                    : ""}
                                </MenuItem>
                              ))}
                            </Select>
                          </div>
                          )}
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          {!!slotSize && (
                            <div className="rounded-full bg-[#f5f3ee] px-3 py-1 text-[12px] font-semibold text-[#5d574c]">
                              {slotSize.sizeName}
                            </div>
                          )}
                          {!hideServiceSubPizzaOptions && !!slotSpice && (
                            <div className="rounded-full bg-[#f5f3ee] px-3 py-1 text-[12px] font-semibold text-[#5d574c]">
                              {slotSpice.modifierName}
                            </div>
                          )}
                          {!hideServiceSubPizzaOptions && !!slotCrust && (
                            <div className="rounded-full bg-[#f5f3ee] px-3 py-1 text-[12px] font-semibold text-[#5d574c]">
                              {slotCrust.modifierName}
                            </div>
                          )}
                        </div>

                        <div className="mt-4 border-t border-[#eee7da] pt-4">
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <div>
                              <div className="text-[14px] font-bold text-[#222]">
                                Customize Toppings
                              </div>
                              <p className="text-[12px] text-gray-500 mt-1">
                                {slotToppingCount > 0
                                  ? `${slotToppingCount} topping${
                                      slotToppingCount > 1 ? "s" : ""
                                    } selected`
                                  : `Choose and update toppings for this ${dealSlotLabel.toLowerCase()}.`}
                              </p>
                            </div>
                            <Button
                              type="button"
                              onClick={() => openDealPizzaCustomize(pizzaIndex)}
                              className="product-popup-place-order-btn shrink-0"
                            >
                              {hasSlotToppingSummary ? "Edit Toppings" : "Customize"}
                            </Button>
                          </div>
                          {hasSlotToppingSummary && (
                            <div className="space-y-1 text-[11px] sm:text-[12px] leading-5 text-[#6f675b]">
                              {(slotDefaults.whole.length > 0 ||
                                slotDefaults.left.length > 0 ||
                                slotDefaults.right.length > 0) && (
                                <div className="space-y-1">
                                  <p className="font-semibold text-[#111]">Default:</p>
                                  {!!slotDefaults.whole.length && (
                                    <p className="pl-3">
                                      <span className="font-semibold text-[#4f493f]">Whole:</span>{" "}
                                      {slotDefaults.whole.join(", ")}
                                    </p>
                                  )}
                                  {!!slotDefaults.left.length && (
                                    <p className="pl-3">
                                      <span className="font-semibold text-[#4f493f]">Left Half:</span>{" "}
                                      {slotDefaults.left.join(", ")}
                                    </p>
                                  )}
                                  {!!slotDefaults.right.length && (
                                    <p className="pl-3">
                                      <span className="font-semibold text-[#4f493f]">Right Half:</span>{" "}
                                      {slotDefaults.right.join(", ")}
                                    </p>
                                  )}
                                </div>
                              )}
                              {(slotExtras.whole.length > 0 ||
                                slotExtras.left.length > 0 ||
                                slotExtras.right.length > 0) && (
                                <div className="space-y-1">
                                  <p className="font-semibold text-[#111]">Extra:</p>
                                  {!!slotExtras.whole.length && (
                                    <p className="pl-3">
                                      <span className="font-semibold text-[#4f493f]">Whole:</span>{" "}
                                      {slotExtras.whole.join(", ")}
                                    </p>
                                  )}
                                  {!!slotExtras.left.length && (
                                    <p className="pl-3">
                                      <span className="font-semibold text-[#4f493f]">Left Half:</span>{" "}
                                      {slotExtras.left.join(", ")}
                                    </p>
                                  )}
                                  {!!slotExtras.right.length && (
                                    <p className="pl-3">
                                      <span className="font-semibold text-[#4f493f]">Right Half:</span>{" "}
                                      {slotExtras.right.join(", ")}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="mt-3 border-t border-[#f3eee4] pt-3">
                            {expandedDealPizzaNotes[pizzaIndex] || !!pizzaConfig.note ? (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between gap-3">
                                  <div>
                                    <div className="text-[13px] font-bold text-[#222]">
                                      {dealSlotLabel} Note
                                    </div>
                                    <p className="text-[11px] leading-4 text-gray-500">
                                      Optional note for this {dealSlotLabel.toLowerCase()} only. Empty note will not show in cart.
                                    </p>
                                  </div>
                                  {!pizzaConfig.note && (
                                    <button
                                      type="button"
                                      className="shrink-0 text-[12px] font-bold text-[#7b7467] underline-offset-2 hover:underline"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setExpandedDealPizzaNotes((prev) => ({
                                          ...prev,
                                          [pizzaIndex]: false,
                                        }));
                                      }}
                                    >
                                      Hide
                                    </button>
                                  )}
                                </div>
                                <TextareaAutosize
                                  className="w-full resize-none rounded-xl border border-[#ece7dc] bg-[#fbfaf7] px-3 py-2 text-[13px] leading-5 outline-none transition placeholder:text-[#8a8173] focus:border-[#d8cdbb]"
                                  minRows={2}
                                  maxRows={3}
                                  placeholder={`Add special request for this ${dealSlotLabel.toLowerCase()}`}
                                  value={pizzaConfig.note || ""}
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={(event) => {
                                    const note = event.target.value;
                                    updateDealPizzaSlotConfig(pizzaIndex, (prev: any) => ({
                                      ...prev,
                                      note,
                                    }));
                                  }}
                                />
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="text-[12px] font-bold text-[#5d574c] underline-offset-2 hover:underline"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setExpandedDealPizzaNotes((prev) => ({
                                    ...prev,
                                    [pizzaIndex]: true,
                                  }));
                                }}
                              >
                                + Add note
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <>
                {hasDealPizzaSlots(selectedProduct) && !selectedProduct.isCreateYourOwn && (
                  <div className="rounded-[20px] border border-[#e4ddd1] bg-white px-4 py-4 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div>
                        <div className="text-[15px] font-bold text-[#222]">
                          {dealSlotLabel} Item
                        </div>
                        <p className="text-[12px] text-gray-500 mt-1">
                          Choose the {dealSlotLabel.toLowerCase()} for this slot.
                        </p>
                      </div>
                      <div
                        className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                          Number(config.id || 0) > 0
                            ? "bg-[#f4f4f2] text-gray-500"
                            : "required-pulse-badge bg-red-600 text-white shadow-[0_0_0_1px_rgba(220,38,38,0.18)]"
                        }`}
                      >
                        Required
                      </div>
                    </div>

                    <Select
                      fullWidth
                      label={`${dealSlotLabel} Item`}
                      value={config.id || ""}
                        onValueChange={(value) => {
                          const nextProductId = Number(value);
                        const chosenPizza = pizzaDropdownOptions.find(
                          (item) => item.productId === nextProductId
                        );
                        const nextSharedSizeId = Number(
                          pizzasInDealRef.current?.[0]?.size ||
                            configRef.current?.size ||
                            0,
                        );
                        const firstSpice = spiceData[0];
                        const firstSpiceName = String(
                          firstSpice?.modifierName || "",
                        ).trim();
                        const firstSpiceLevel = firstSpiceName
                          ? {
                              spiceId: firstSpice?.modifierId || 1,
                              spicename: firstSpiceName,
                            }
                          : null;

                        updateCurrentPizzaConfig((prev: any) => ({
                          ...prev,
                          id: nextProductId,
                          pizzaName: chosenPizza?.productName || "",
                          pizzaProductCode: chosenPizza?.productCode || "",
                          size:
                            getDealPizzaCount(selectedProduct) > 1
                              ? nextSharedSizeId
                              : prev.size,
                          crust: 0,
                          spiceLevel: firstSpiceName,
                          existingSpiceLevel: firstSpiceLevel,
                          variant: [],
                          savedCartModifiers: [],
                          modifications: undefined,
                        }));
                        if (getDealPizzaCount(selectedProduct) <= 1) {
                          setSizes([]);
                        }
                        setCrusts([]);
                      }}
                    >
                      {dealPizzaOptionsLoading && !pizzaDropdownOptions.length && (
                        <MenuItem disabled value="">
                          Loading pizzas...
                        </MenuItem>
                      )}
                      {pizzaDropdownOptions.map((item: any) => (
                        <MenuItem key={item.productId} value={item.productId}>
                          {item.productName}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                )}

                <div className="rounded-[20px] border border-[#e4ddd1] bg-white px-4 py-4 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
                  <div
                    className={`grid grid-cols-1 gap-3 ${
                      hideServiceSubPizzaOptions
                        ? "md:grid-cols-[minmax(155px,260px)]"
                        : "md:grid-cols-[minmax(155px,1fr)_minmax(170px,1.15fr)_minmax(170px,1.15fr)]"
                    }`}
                  >
                    <div>
                      <div className="text-[13px] font-bold text-[#222] mb-2">
                        Size
                      </div>
                      <Select
                        fullWidth
                        label=""
                        value={config.size || ""}
                        disabled={isSharedDealSizeLocked}
                        inputProps={{ "aria-label": "Size" }}
                        onValueChange={(value) => {
                          const nextSizeId = Number(value);
                          const chosenSize = sizes.find(
                            (item) => Number(item?.sizeId) === nextSizeId,
                          );

                          if (
                            getDealPizzaCount(selectedProduct) > 1 &&
                            currentPizzaIndex === 0
                          ) {
                            applySharedDealSize(nextSizeId, chosenSize);
                            return;
                          }

                          updateCurrentPizzaConfig((prev: any) => ({
                            ...prev,
                            size: nextSizeId,
                            existingPizzaSize: chosenSize
                              ? {
                                  pizzasizeId: chosenSize.sizeId,
                                  size: chosenSize.sizeName,
                                  sizePriceX1: Number(chosenSize?.sizePriceX1 || 0),
                                  sizePriceX2: Number(chosenSize?.sizePriceX2 || 0),
                                  sizePriceX3: Number(chosenSize?.sizePriceX3 || 0),
                                  sizePriceX4: Number(chosenSize?.sizePriceX4 || 0),
                                  sizePriceX5: Number(chosenSize?.sizePriceX5 || 0),
                                }
                              : prev.existingPizzaSize,
                            existingSizePrice: Number(getSizePrice(chosenSize) || 0),
                            savedCartModifiers: [],
                            modifications: undefined,
                          }));
                        }}
                      >
                        {sizes.map((item) => (
                          <MenuItem key={item.sizeId} value={item.sizeId}>
                            {item.sizeName} [
                            {formatMoney(
                              selectedProduct?.isPizza &&
                                !selectedProduct?.isCreateYourOwn &&
                                !hasDealPizzaSlots(selectedProduct)
                                ? getPizzaSizeQtyPrice(item, 1)
                                : getSizePrice(item) || 0,
                            )}
                            ]
                          </MenuItem>
                        ))}
                      </Select>
                      {isSharedDealSizeLocked && (
                        <p className="mt-2 text-[11px] text-gray-500">
                          Size is shared from {dealSlotLabel} 1 for this deal.
                        </p>
                      )}
                    </div>

                    {!hideServiceSubPizzaOptions && (
                    <div>
                      <div className="text-[13px] font-bold text-[#222] mb-2">
                        Special Instructions
                      </div>
                      <Select
                        fullWidth
                        label=""
                        value={config.spiceLevel || ""}
                        inputProps={{ "aria-label": "Select Spice" }}
                        onValueChange={(value) => {
                          const chosenSpice = spiceData.find(
                            (item: any) => item?.modifierName === value,
                          );

                          updateCurrentPizzaConfig((prev: any) => ({
                            ...prev,
                            spiceLevel: value,
                            existingSpiceLevel: chosenSpice
                              ? {
                                  spiceId: chosenSpice.modifierId || 1,
                                  spicename: chosenSpice.modifierName || value,
                                }
                              : prev.existingSpiceLevel,
                          }));
                        }}
                      >
                        {spiceData.map((item: any) => (
                          <MenuItem
                            key={item.modifierId || item.modifierName}
                            value={item.modifierName}
                          >
                            {item.modifierName}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                    )}

                    {!hideServiceSubPizzaOptions && (
                    <div>
                      <div className="text-[13px] font-bold text-[#222] mb-2">
                        Crusts
                      </div>
                      <Select
                        fullWidth
                        label=""
                        value={selectedCrust ? Number(selectedCrust.modifierId) : ""}
                        inputProps={{ "aria-label": "Crusts" }}
                        onValueChange={(value) => {
                          const nextCrustId = Number(value);
                          const chosenCrust = crusts.find(
                            (item) => Number(item?.modifierId) === nextCrustId,
                          );

                          updateCurrentPizzaConfig((prev: any) => ({
                            ...prev,
                            crust: nextCrustId,
                            existingPizzaCrust: chosenCrust
                              ? {
                                  crustId: chosenCrust.modifierId,
                                  crustName: chosenCrust.modifierName,
                                  crustPrice: chosenCrust.modifierCost,
                                }
                              : prev.existingPizzaCrust,
                          }));
                        }}
                      >
                        {crusts.map((item) => (
                          <MenuItem key={item.modifierId} value={Number(item.modifierId)}>
                            {item.modifierName}
                            {item.modifierCost
                              ? ` [+${formatMoney(item.modifierCost)}]`
                              : ""}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                    )}
                  </div>

                  {selectedSizeOfferPrices.length > 1 && (
                    <div className="mt-3 flex items-center gap-2 overflow-hidden rounded-[10px] border border-[#ffb4b4] bg-[#fff5f5] px-2.5 py-2">
                      <div className="relative z-10 shrink-0 rounded-l-[7px] bg-[#f10505] px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-normal text-white after:absolute after:right-[-7px] after:top-0 after:h-0 after:w-0 after:border-y-[12px] after:border-l-[7px] after:border-y-transparent after:border-l-[#f10505]">
                        Offer
                      </div>
                      {offerScrollState.canScrollLeft && (
                        <button
                          type="button"
                          onClick={() =>
                            offerScrollRef.current?.scrollBy({
                              left: -130,
                              behavior: "smooth",
                            })
                          }
                          className="ml-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[12px] font-black text-[#f10505] shadow-sm ring-1 ring-[#ffd1d1] transition hover:bg-[#fff0f0]"
                          aria-label="Scroll offers left"
                        >
                          &lt;
                        </button>
                      )}
                      <div
                        ref={offerScrollRef}
                        className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto whitespace-nowrap pb-0.5 [&::-webkit-scrollbar]:hidden"
                        style={{
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                        }}
                      >
                        {selectedSizeOfferPrices.map((offer) => (
                          <button
                            type="button"
                            key={`${selectedSize?.sizeId || "size"}-${offer.qty}`}
                            onClick={() => updatePopupQuantity(offer.qty)}
                            className={`rounded-full px-2 py-1 text-[11px] font-bold shadow-sm ring-1 transition ${
                              Number(config.qty || 1) === offer.qty
                                ? "bg-[#f10505] text-white ring-[#f10505]"
                                : "bg-white text-[#d90404] ring-[#ffd1d1] hover:bg-[#fff0f0]"
                            }`}
                          >
                            {offer.qty} x {formatMoney(offer.price)}
                          </button>
                        ))}
                      </div>
                      {offerScrollState.canScrollRight && (
                        <button
                          type="button"
                          onClick={() =>
                            offerScrollRef.current?.scrollBy({
                              left: 130,
                              behavior: "smooth",
                            })
                          }
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[12px] font-black text-[#f10505] shadow-sm ring-1 ring-[#ffd1d1] transition hover:bg-[#fff0f0]"
                          aria-label="Scroll offers right"
                        >
                          &gt;
                        </button>
                      )}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {!!selectedSize && (
                      <div className="rounded-full bg-[#f5f3ee] px-3 py-1 text-[12px] font-semibold text-[#5d574c]">
                        {selectedSize.sizeName}
                      </div>
                    )}
                    {!hideServiceSubPizzaOptions && !!selectedSpice && (
                      <div className="rounded-full bg-[#f5f3ee] px-3 py-1 text-[12px] font-semibold text-[#5d574c]">
                        {selectedSpice.modifierName}
                      </div>
                    )}
                    {!hideServiceSubPizzaOptions && !!selectedCrust && (
                      <div className="rounded-full bg-[#f5f3ee] px-3 py-1 text-[12px] font-semibold text-[#5d574c]">
                        {selectedCrust.modifierName}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 border-t border-[#eee7da] pt-4">
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[14px] font-bold text-[#222]">
                          Customize Toppings
                        </div>
                        <p className="text-[12px] text-gray-500 mt-1">
                          {totalModifications > 0
                            ? `${totalModifications} topping${totalModifications > 1 ? "s" : ""} selected`
                            : `Choose and update toppings for this ${dealSlotLabel.toLowerCase()}.`}
                        </p>
                      </div>
                      <Button
                        type="button"
                        onClick={openPizzaCustomize}
                        className="product-popup-place-order-btn shrink-0"
                      >
                        {hasTouchedToppings ? "Edit Toppings" : "Customize"}
                      </Button>
                    </div>
                    <div className="w-full">
                      {hasDefaultToppingSummary && (
                        <div className="mt-2 space-y-1 text-[11px] sm:text-[12px] leading-5 text-[#6f675b]">
                          <p className="font-semibold text-[#111]">Default:</p>
                          {!!toppingSummary.defaults.whole.length && (
                            <p className="pl-3">
                              <span className="font-semibold text-[#4f493f]">Whole:</span>{" "}
                              {toppingSummary.defaults.whole.join(", ")}
                            </p>
                          )}
                          {!!toppingSummary.defaults.left.length && (
                            <p className="pl-3">
                              <span className="font-semibold text-[#4f493f]">Left Half:</span>{" "}
                              {toppingSummary.defaults.left.join(", ")}
                            </p>
                          )}
                          {!!toppingSummary.defaults.right.length && (
                            <p className="pl-3">
                              <span className="font-semibold text-[#4f493f]">Right Half:</span>{" "}
                              {toppingSummary.defaults.right.join(", ")}
                            </p>
                          )}
                        </div>
                      )}
                      {hasExtraToppingSummary && (
                        <div className="mt-1 space-y-1 text-[11px] sm:text-[12px] leading-5 text-[#6f675b]">
                          <p className="font-semibold text-[#111]">Extra:</p>
                          {!!toppingSummary.extras.whole.length && (
                            <p className="pl-3">
                              <span className="font-semibold text-[#4f493f]">Whole:</span>{" "}
                              {toppingSummary.extras.whole.join(", ")}
                            </p>
                          )}
                          {!!toppingSummary.extras.left.length && (
                            <p className="pl-3">
                              <span className="font-semibold text-[#4f493f]">Left Half:</span>{" "}
                              {toppingSummary.extras.left.join(", ")}
                            </p>
                          )}
                          {!!toppingSummary.extras.right.length && (
                            <p className="pl-3">
                              <span className="font-semibold text-[#4f493f]">Right Half:</span>{" "}
                              {toppingSummary.extras.right.join(", ")}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
              </div>
            )}

            {/* === ADDED BY SAMPATH – 2026-01-12 === */}
            {/* REMOVE STEPS UI: show ALL variant levels in ONE PAGE (scroll) */}
            {!!variants.length && (
              <div className="flex flex-col gap-4">
                {variants.map((level: any, idx: number) => {
                  const selectedForLevel = getVariantAt(idx);
                  const invalid = !isVariantLevelValid(idx);
                  const min = Number(level?.mandatoryLevel || 0);
                  const max = Number(level?.qtyLevel || 0);

                  return (
                    // === ADDED BY SAMPATH – 2026-03-16 ===
                    // Cleaner option group cards like mobile food ordering apps
                    <div
                      key={idx}
                      className="border-b border-[#ddd8cd] pb-4 last:border-b-0"
                    >
                    {/* === END SAMPATH === */}
                      <div className="flex items-start justify-between mb-2 gap-3">
                        <div>
                          <div className="text-[15px] font-bold text-[#222]">
                            {level?.levelName ||
                              level?.name ||
                              level?.nameLevel ||
                              level?.title ||
                              `Option ${idx + 1}`}
                          </div>
                          <p className="text-[12px] text-gray-500 mt-1">
                            {min > 0 ? `(Required)` : `(Optional)`}
                            {max > 0 ? ` • Select up to ${max}` : ""}
                            {min > 0 ? ` • Minimum ${min}` : ""}
                          </p>
                        </div>

                        <div
                          className={`text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${
                            min > 0 && invalid
                              ? "required-pulse-badge bg-red-600 text-white shadow-[0_0_0_1px_rgba(220,38,38,0.18)]"
                              : min > 0
                              ? "bg-[#eef4ef] text-[#24513e]"
                              : "bg-[#f4f4f2] text-gray-500"
                          }`}
                        >
                          {min > 0 ? "Required" : "Optional"}
                        </div>
                      </div>

                      {invalid && (
                        <p className="text-red-500 text-[12px] leading-4 mb-2">
                          Select maximum {level?.qtyLevel} and minimum{" "}
                          {level?.mandatoryLevel}.
                        </p>
                      )}

                      <VariantSelection
                        selected={selectedForLevel}
                        setSelected={(val: any) => setVariantAt(idx, val)}
                        // keep prop but no step navigation needed
                        moveToNextStep={() => {}}
                        option={level}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {/* === END SAMPATH === */}

            {!hasDealPizzaSlots(selectedProduct) && (
              <div className="border-t border-[#ddd8cd] pt-4">
                <div className="text-[15px] font-bold text-[#222]">
                  User Preferences
                </div>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="text-[14px] font-bold text-[#222]">
                    Add Special Instructions
                  </div>
                  <div className="text-[12px] sm:text-[13px] font-medium text-gray-500">
                    List any special requests
                  </div>
                </div>

                <TextareaAutosize
                  className="mt-3 w-full border border-[#f0f0f0] bg-[#f7f7f7] p-4 rounded-xl outline-none focus:border-[#d6d6d6] transition text-[14px] sm:text-[15px] h-[124px] min-h-[124px] max-h-[124px] resize-none overflow-y-auto placeholder:text-[#6b6b6b] placeholder:italic leading-7"
                  minRows={3}
                  maxRows={3}
                  placeholder={
                    autoRemovedToppingNotes.length || autoToppingSummaryNote
                      ? "Add more special requests below the topping notes."
                      : "Add any special requests (e.g., food allergies, extra spicy, etc.) and the store will do its best to accommodate you."
                  }
                  value={config.note}
                  onChange={(e) =>
                    updateCurrentPizzaConfig((v: any) => ({ ...v, note: e.target.value }))
                  }
                />
                {!!autoRemovedToppingNotes.length && (
                  <div className="mt-3 rounded-xl border border-[#ece7dc] bg-[#f7f7f7] p-4">
                    <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-[#7b7467]">
                      Auto Topping Notes
                    </div>
                    <div className="whitespace-pre-wrap text-[14px] sm:text-[15px] font-semibold leading-7 text-[#222]">
                      {autoRemovedToppingNotes.join("\n")}
                    </div>
                    <p className="mt-2 text-[12px] text-gray-500">
                      These topping notes are added automatically and cannot be edited directly.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* === ADDED BY SAMPATH – 2026-01-12 === */}
            {/* Hide old Quantity block (we move Qty controls to the footer next to Add to Cart) */}
            <div className="hidden">
              <div className="rounded-xl bg-gray-50 p-3 flex items-center justify-between">
                <div className="text-[13px] font-semibold text-gray-800">
                  Quantity
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    className="!px-2 !min-w-0 !rounded-full !bg-white !shadow-sm !border !border-gray-200"
                    onClick={() =>
                      config.qty > 1 &&
                      updatePopupQuantity(Number(config.qty || 1) - 1)
                    }
                  >
                    <RemoveIcon className="!text-gray-800" />
                  </Button>

                  <Input
                    size="small"
                    className="max-w-[70px]"
                    inputProps={{ style: { textAlign: "center" } }}
                    value={config.qty}
                    onValueChange={(value) => {
                      let qty = +value;
                      if (isNaN(qty) || qty < 1) qty = 1;
                      if (qty > 9999) qty = 9999;
                      updatePopupQuantity(qty);
                    }}
                  />

                  <Button
                    className="!px-2 !min-w-0 !rounded-full !bg-white !shadow-sm !border !border-gray-200"
                    onClick={() =>
                      updatePopupQuantity(
                        Number(config.qty || 1) < 9999
                          ? Number(config.qty || 1) + 1
                          : Number(config.qty || 1),
                      )
                    }
                  >
                    <AddIcon className="!text-gray-800" />
                  </Button>
                </div>
              </div>
            </div>
            {/* === END SAMPATH === */}
          </div>
          </div>

          {/* === ADDED BY SAMPATH – 2026-01-12 === */}
          {/* Add to cart + Qty (+/-) in ONE LINE like your image (theme colors + transparent mix) */}
          {/* === ADDED BY SAMPATH – 2026-03-16 === */}
          {/* Better sticky footer with app-style safe area for mobile */}
          <div
            className="sticky bottom-0 bg-white px-4 sm:px-5 pt-3 border-t border-gray-100"
            style={{ paddingBottom: isMobilePopup ? "calc(env(safe-area-inset-bottom, 0px) + 12px)" : "16px" }}
          >
          {/* === END SAMPATH === */}
            <div className="flex items-center gap-3">
              {/* Qty control (compact pill) */}
              <div
                // === ADDED BY SAMPATH – 2026-03-16 ===
                // Rounded light qty pill like reference
                className="flex items-center rounded-2xl border border-black/8 overflow-hidden shadow-sm"
                // === END SAMPATH ===
                style={{ backgroundColor: themePrimaryBg }}
              >
                <button
                  type="button"
                  className="h-11 w-11 flex items-center justify-center transition"
                  style={{ backgroundColor: "transparent" }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as any).style.backgroundColor =
                      themePrimaryBgHover)
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as any).style.backgroundColor =
                      "transparent")
                  }
                  onClick={() =>
                    config.qty > 1 && updatePopupQuantity(Number(config.qty || 1) - 1)
                  }
                  aria-label="Decrease quantity"
                >
                  <RemoveIcon
                    className="!text-[20px]"
                    style={{ color: themePrimarySolid }}
                  />
                </button>

                <div className="min-w-[44px] text-center font-semibold text-[14px] text-black select-none">
                  {Math.max(1, Number(config.qty || 1))}
                </div>

                <button
                  type="button"
                  className="h-11 w-11 flex items-center justify-center transition"
                  style={{ backgroundColor: "transparent" }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as any).style.backgroundColor =
                      themePrimaryBgHover)
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as any).style.backgroundColor =
                      "transparent")
                  }
                  onClick={() =>
                    updatePopupQuantity(
                      Number(config.qty || 1) < 9999
                        ? Number(config.qty || 1) + 1
                        : Number(config.qty || 1),
                    )
                  }
                  aria-label="Increase quantity"
                >
                  <AddIcon
                    className="!text-[20px]"
                    style={{ color: themePrimarySolid }}
                  />
                </button>
              </div>

              {/* Add to Cart button (same row) */}
              <Button
                disabled={!areAllRequiredSelectionsComplete}
                // === ADDED BY SAMPATH – 2026-03-16 ===
                // More premium rounded CTA like sample popup
                className="product-popup-place-order-btn flex-1 flex flex-col justify-center"
                // === END SAMPATH ===
                onClick={() => {
                  addItemToCart();
                }}
              >
                <span className="w-full flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2">
                    {cartBtnLoading && (
                      <CircularProgress size={20} className="!text-themeSelColor" />
                    )}
                    Add to Cart
                  </span>
                  <span className="text-[14px] font-semibold">{formatMoney(uiTotalPrice)}</span>
                </span>
              </Button>
            </div>

            {!!footerValidationMessage && (
              <div className="mt-2 w-full overflow-hidden text-center text-[12px] leading-5 text-red-500">
                <div className="deal-validation-marquee inline-block min-w-full whitespace-nowrap">
                  {footerValidationMessage}.
                </div>
              </div>
            )}

          </div>
          {/* === END SAMPATH === */}
        </div>
      )}

      {/* === ADDED BY SAMPATH – 2026-01-05 === */}
      {/* Image preview popup */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="md"
        fullWidth
        // === ADDED BY SAMPATH – 2026-03-16 ===
        // Full screen image preview on mobile
        fullScreen={isMobilePopup}
        // === END SAMPATH ===

        // === ADDED BY SAMPATH – 2026-01-13 ===
        BackdropProps={{
          style: {
            backdropFilter: "blur(12px)",
            backgroundColor: "rgba(0,0,0,0.75)",
          },
        }}
        // === END SAMPATH ===

        PaperProps={{
          className: isMobilePopup
            ? "overflow-hidden bg-black/90"
            : "rounded-2xl overflow-hidden bg-black/90",
          sx: {
            margin: isMobilePopup ? 0 : 1.5,
            width: isMobilePopup ? "100%" : undefined,
            maxWidth: isMobilePopup ? "100%" : undefined,
            height: isMobilePopup ? "100dvh" : "auto",
            maxHeight: isMobilePopup ? "100dvh" : "90vh",
          },
        }}
      >
        <div className="relative">
          <button
            onClick={() => setImagePreviewOpen(false)}
            // === ADDED BY SAMPATH – 2026-03-16 ===
            className="absolute top-3 left-3 h-11 w-11 rounded-full bg-white/95 shadow flex items-center justify-center hover:bg-gray-100 transition z-10"
            // === END SAMPATH ===
            aria-label="Close image"
            type="button"
          >
            ✕
          </button>

          <img
            src={selectedProduct?.imageUrl || noImage}
            alt="preview"
            className="w-full max-h-[100dvh] object-contain bg-black"
          />
        </div>
      </Dialog>
      {/* === END SAMPATH === */}

      <ModificationPopUp
        open={showModification}
        setOpen={setshowModification}
        onSubmit={(modifications) => {
          flushSync(() => {
            cacheModifierPrices(modifications);

            debugDealEdit("TOPPING_POPUP_SAVE", {
              currentPizzaIndex,
              configBefore: configRef.current,
              modifications,
            });

            const toppingTotal = getSelectedModifierCategories(modifications).filter(
              ({ items }: any) => items.length > 0,
            );

            updateCurrentPizzaConfig((v: any) => {
              const nextPizzaConfig = !!toppingTotal.length
                ? { ...v, modifications, modificationsDirty: true, savedCartModifiers: [] }
                : { ...v, modificationsDirty: true, savedCartModifiers: [] };

              const finalPizzaConfig = {
                ...nextPizzaConfig,
                modifications: !!toppingTotal.length ? modifications : undefined,
              };

              debugDealEdit("TOPPING_POPUP_SAVE_APPLIED", {
                currentPizzaIndex,
                toppingTotal,
                updatedSlot: finalPizzaConfig,
              });

              return finalPizzaConfig;
            });

            setshowModification(undefined);
          });
        }}
      />
      <style>{`
        .deal-popup-scroll{
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,.98) rgba(255,255,255,.75);
        }
        .deal-popup-scroll::-webkit-scrollbar{
          width: 10px;
        }
        .deal-popup-scroll::-webkit-scrollbar-track{
          background: rgba(255,255,255,.75);
          border-radius: 999px;
        }
        .deal-popup-scroll::-webkit-scrollbar-thumb{
          background: rgba(255,255,255,.98);
          border-radius: 999px;
          border: 2px solid rgba(255,255,255,.75);
        }
        .deal-popup-scroll::-webkit-scrollbar-thumb:hover{
          background: rgba(255,255,255,1);
        }
        .deal-validation-marquee{
          animation: deal-validation-marquee 12s linear infinite;
        }
        .deal-validation-marquee:hover{
          animation-play-state: paused;
        }
        .required-pulse-badge{
          animation: required-pulse-badge 1.05s ease-in-out infinite;
        }
        @keyframes required-pulse-badge{
          0%, 100%{
            background-color: #dc2626;
            box-shadow: 0 0 0 0 rgba(220,38,38,.36);
          }
          50%{
            background-color: #ef4444;
            box-shadow: 0 0 0 5px rgba(220,38,38,.14);
          }
        }
        @keyframes deal-validation-marquee{
          0%, 12%{
            transform: translateX(0);
          }
          88%, 100%{
            transform: translateX(calc(-100% + 100vw - 48px));
          }
        }
        @media (min-width: 640px){
          @keyframes deal-validation-marquee{
            0%, 12%{
              transform: translateX(0);
            }
            88%, 100%{
              transform: translateX(calc(-100% + 590px));
            }
          }
        }
        @media (prefers-reduced-motion: reduce){
          .deal-validation-marquee,
          .required-pulse-badge{
            animation: none;
          }
        }
      `}</style>
    </Dialog>
  );
};

export default ProductPopUp;
